import type { DeviceKeyJwk } from './proof';

/**
 * Wire types for the backend REST surface (`/api/v1`).
 *
 * These mirror the Go backend's JSON contracts. They are the shared vocabulary
 * for the UI, the mock adapter and (eventually) plugins — see src/plugins/.
 *
 * These types are the **camelCase image of `docs/plan/10-api-contract.md` §6**.
 * The wire itself is `snake_case` (ADR 0009); `HttpTransport` converts
 * mechanically through `./wire.ts`, so nothing above the transport — not this
 * file, not the mock adapter, not a component — ever sees a `snake_case` key.
 * The one deliberate exception is `ApiErrorBody`, which is read verbatim.
 *
 * TODO(api): regenerate from the backend's OpenAPI document once it exists,
 * rather than hand-maintaining this file. The error-code list is already
 * generated — see `./errors.generated.ts`.
 */

import type { ApiErrorCodeLike } from './errors';

/* ------------------------------------------------------------------ common */

export type ISODateTime = string;

export interface Paginated<T> {
  items: T[];
  /** Opaque cursor for the next page; absent when the list is exhausted. */
  nextCursor?: string;
  /** Total matching items when the backend can cheaply compute it. */
  total?: number;
  /**
   * Search only: true when some folders were skipped (a degraded account, a
   * connection cap) and the page is therefore incomplete. Doc 10 §1.5.
   */
  partial?: boolean;
  /** Message lists only: the order actually applied, after defaulting. */
  sort?: MessageSortOrder;
  /**
   * Message lists only: the orders THIS mail server can produce, from the
   * capability set probed after authentication. A sort control offers these
   * and nothing else -- IMAP SORT is an optional extension, and a server that
   * lacks it can only do arrival order.
   */
  sortsAvailable?: MessageSortOrder[];
}

export interface PageQuery {
  cursor?: string;
  limit?: number;
}

/**
 * Body of the `error` member of a non-2xx response.
 *
 * Mirrors Go `httpapi.ErrorBody` in `backend/internal/httpapi/errors.go`
 * exactly, field names included (`request_id` is snake_case on the wire).
 */
export interface ApiErrorBody {
  code: ApiErrorCodeLike;
  message: string;
  /**
   * Extra structured detail. On a 429 the backend puts the lockout window in
   * `details.retry_after_sec` (and mirrors it in the `Retry-After` header).
   */
  details?: Record<string, unknown>;
  request_id?: string;
}

/**
 * The single JSON error envelope every endpoint returns.
 * Mirrors Go `httpapi.ErrorEnvelope`:
 * `{"error":{"code":"...","message":"...","details":{},"request_id":"..."}}`
 */
export interface ApiErrorEnvelope {
  error: ApiErrorBody;
}

/* -------------------------------------------------------------------- auth */

export interface LoginRequest {
  /** Full email address; the backend resolves the mail host from it. */
  email: string;
  password: string;
  /** Opt into a longer-lived refresh cookie. */
  remember?: boolean;
  /**
   * Public half of this browser's device key (ADR 0040), enrolled at login.
   *
   * The login request is itself signed by the matching private key, so a
   * browser with broken WebCrypto is discovered *before* a session exists
   * rather than on the first mail request after it. Omitted only when this
   * browser cannot hold a key at all: under `binding: preferred` that buys an
   * unbound session (short, never remembered, banner); under `required` the
   * login is refused `403 device_key_required`.
   */
  deviceKey?: DeviceKeyJwk;
  /** True when running as an installed PWA — recorded as `app` on the record. */
  app?: boolean;
}

/**
 * Public JWK of the device key, exactly the four RFC 7638 members.
 *
 * Re-exported from `proof.ts`, which owns the canonical spelling because the
 * thumbprint is computed over it.
 */
export type { DeviceKeyJwk };

/** `POST /auth/rebind` — re-enrol a device key on a live session. */
export interface RebindRequest {
  /** The mailbox password. The one thing an offline cookie thief lacks. */
  password: string;
  deviceKey: DeviceKeyJwk;
}

/** `POST /events/ticket` — single-use, short-lived, never stored (ADR 0041). */
export interface StreamTicket {
  ticket: string;
  expiresAt: ISODateTime;
}

/** `POST /auth/sessions/revoke-others` — the body. */
export interface RevokeOthersRequest {
  password: string;
  /** This browser's push subscription endpoint, so the server keeps its row. */
  keepEndpoint?: string;
}

/** `POST /auth/sessions/revoke-others`. */
export interface RevokeOthersResult {
  revokedCount: number;
}

/**
 * One live session of this owner (`GET /auth/sessions`, ADR 0045).
 *
 * Every field here is deliberately coarse. `client` is a server-parsed
 * `<browser> on <OS>` label — the raw `User-Agent` is never stored, because it
 * is a device fingerprint and, on an old build, an advertisement of unpatched
 * software. `network` is masked *on the wire* (IPv4 `/24`, IPv6 `/48`), which
 * is why it is called a network and not a location: on this deployment every
 * LAN client arrives as one address anyway. Both are hints for recognising
 * your own devices, not identification.
 */
export interface SessionSummary {
  /** `Session.ID` — the loggable id, never a credential. */
  id: string;
  /** True for the session making the request. */
  current: boolean;
  /** False for a session created without a device key (`binding: preferred`). */
  bound: boolean;
  /** `remember` was ticked: 30 d idle / 90 d absolute (ADR 0050). */
  remembered: boolean;
  /** Coarse label, e.g. `Firefox on macOS`. Server-derived. */
  client: string;
  /** True when created by an installed PWA. */
  app: boolean;
  /** Masked address, e.g. `192.168.1.x`. */
  network: string;
  /** The caller's own resolved address is in the same masked range. */
  sameNetwork: boolean;
  createdAt: ISODateTime;
  /**
   * Coalesced server-side; up to 5 minutes stale by design, and **absent**
   * until a session has been seen since the field existed (the backend omits
   * it rather than inventing a timestamp).
   */
  lastSeenAt?: ISODateTime;
  /** Set when the session was re-verified by a password rebind (ADR 0044). */
  reboundAt?: ISODateTime | null;
  expiresAt: ISODateTime;
  absoluteExpiresAt: ISODateTime;
}

/**
 * The authenticated session as the *frontend* can observe it.
 *
 * There is deliberately no token field. Session material lives in the
 * `__Host-ols_session` / `__Host-ols_key` cookies, both `HttpOnly` (ADR 0001),
 * so JavaScript cannot read it and must not hold a copy: a token in
 * JS-reachable storage is exactly the XSS-exfiltration hazard the cookie split
 * exists to remove, and v1 renders untrusted HTML mail.
 *
 * This is therefore a *description* of the session returned by
 * `GET /auth/session`, not a credential.
 */
export interface Session {
  /** Sliding idle expiry. */
  expiresAt: ISODateTime;
  /** Hard cap: refresh cannot push `expiresAt` past this. */
  absoluteExpiresAt: ISODateTime;
  account: SessionAccount;
  /**
   * `Session.ID` — the id the session list marks as *This device*.
   *
   * Not a credential and not secret: it is the first 16 hex characters of the
   * token *hash*, which is what the backend already logs.
   */
  sessionId?: string;
  /**
   * False for a session that was created without a device key and is therefore
   * protected by cookies alone (ADR 0042). Never inferred and never assumed:
   * `undefined` means a backend that predates binding, which is an unbound
   * session that does not yet know the word.
   */
  bound?: boolean;
  /**
   * The server's clock at the moment it answered.
   *
   * The proof's `iat` is stamped from this, not from the device clock, so a
   * phone whose clock is hours wrong still signs inside the ±120 s window
   * (docs/plan/14 §1.6).
   */
  serverTime?: ISODateTime;
  /**
   * The owner is on the server's `admin.emails` list (ADR 0030), derived from
   * config on every serialisation. It decides whether the Admin entry is
   * DRAWN, and nothing else: every `/admin/*` route re-checks on the server
   * and answers a non-admin `404`, so a forged `true` here buys a page of
   * errors, not a single rule.
   */
  admin?: boolean;
  /**
   * When the current step-up elevation lapses, or `null` without one
   * (ADR 0032). Informational: the server is the only judge of elevation, and
   * a write it refuses with `403 step_up_required` prompts regardless.
   */
  elevatedUntil?: ISODateTime | null;
  /**
   * The user-content origin message bodies are framed from (ADR 0078), absent
   * when the deployment has none. The page uses it only when its own build
   * names the same origin (`resolveContentOrigin`): the app shell's CSP has to
   * allow framing it, so a mismatch keeps the srcdoc renderer.
   */
  contentOrigin?: string;
}

/**
 * `POST /accounts/{id}/messages/{messageId}/render` (ADR 0078): a single-use
 * URL on the user-content origin for one rendered document. The page adds its
 * channel id as the fragment and frames it.
 */
export interface RenderTicket {
  url: string;
  renderId: string;
  expiresAt: ISODateTime;
}

/**
 * `GET …/renders/{renderId}/stats`: why the images of one render that did not
 * load did not load. The frame can see that an image failed, not its status.
 */
export interface RenderStats {
  failed: number;
  refused: number;
  svgBlocked: number;
}

/** `POST …/parts/{partId}/link`: a short-lived URL on the user-content origin. */
export interface PartLink {
  url: string;
  expiresAt: ISODateTime;
}

/** `POST /accounts/{id}/password` — the primary mailbox's password change. */
export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

/**
 * The answer to a password change: the **rotated** session.
 *
 * The cookie trio is re-issued on success (the old one is dead), so this is a
 * `Session` like login's, plus how many other sessions the change signed out.
 */
export interface ChangePasswordResult extends Session {
  revokedSessions: number;
}

/**
 * Who the session belongs to (ADR 0010).
 *
 * There is no user database: authentication is pass-through against the mail
 * host, so the only durable identifier a person has is their normalised
 * primary email address. Hence **no `id` of its own** (a hash of the email
 * would be a second spelling of the email) and **no `displayName`** (IMAP has
 * no profile to populate one from; `Account.displayName` exists, is empty
 * unless the owner sets it, and the UI falls back to the address).
 *
 * `id` is the *primary account's* id — `uuid5(NS_OLS_ACCOUNT, "primary:" +
 * email)`, derived from the email alone, so it is the same with or without
 * Postgres and across restarts. It is what `primaryAccountId` used to be, and
 * it is what identity comparisons ("is this still the same person?") use.
 */
export interface SessionAccount {
  /** The primary (login) mailbox's account id. Opaque; never parsed. */
  id: string;
  /** Normalised: trimmed and lowercased. */
  email: string;
  domain: string;
  /** Mail host the routing rule resolved this address to. */
  authHost: string;
}

/* ---------------------------------------------------------------- accounts */

export type AccountStatus =
  | 'connected'
  | 'connecting'
  | 'degraded'
  | 'error'
  | 'reauth-required';

/**
 * A connected mailbox. One session owner may hold several; there is no unified
 * inbox in v1 — each account has its own folder tree and views.
 */
export interface Account {
  id: string;
  email: string;
  displayName: string;
  /** Mail host this account authenticates against (from the routing table). */
  host: string;
  isPrimary: boolean;
  status: AccountStatus;
  /** Set when status is 'degraded' / 'error' / 'reauth-required'. */
  statusMessage?: string;
  /** When the breaker will next probe this account; set while 'degraded'. */
  nextRetryAt?: ISODateTime;
  color?: string;
  quota?: Quota;
  capabilities: AccountCapabilities;
  /** Additional addresses that deliver here (G12); the account's own is not repeated. */
  aliases: string[];
  /**
   * The attachment ceilings a send from this account is held to (ADR 0084
   * D7), so the composer decides before uploading a byte rather than
   * learning from a 413 afterwards. Absent when the deployment stages no
   * attachments at all.
   */
  limits?: AccountLimits;
  /**
   * The provider catalogue id (`gmail`, `yahoo`…) of a mailbox on another
   * provider (ADR 0107). Absent for our own mailboxes and the primary. Such a
   * mailbox has mail only: no filters, out of office, aliases, password
   * change, calendar or contacts.
   */
  provider?: string;
  /** The provider's display name ("Gmail"), sent with `provider`. */
  providerName?: string;
  /**
   * How this mailbox signs in (ADR 0109): a stored password, or an OAuth
   * token from Google or Microsoft. Absent from an older backend, which only
   * has passwords. An `oauth2` mailbox is reconnected through
   * `POST /accounts/oauth/start` with its `account_id`, never with a
   * password: `verify` on it answers `400 use_oauth`.
   */
  auth?: AccountAuth;
}

/** `auth` on an account (ADR 0109). */
export type AccountAuth = 'password' | 'oauth2';

/** A way a provider accepts a mailbox (ADR 0109), in the server's preference order. */
export type ProviderAuthMethod = 'oauth2' | 'app_password';

/** Who runs the sign-in for an `oauth2` provider (ADR 0109). */
export type OAuthIssuer = 'google' | 'microsoft';

/**
 * One entry of the compiled-in provider catalogue (ADR 0107), from
 * `GET /accounts/providers`. The mail hosts are the catalogue's, never the
 * user's: they are shown, not chosen.
 */
export interface ExternalProvider {
  id: string;
  name: string;
  /** The first usable method, kept for older clients; read {@link authMethods} instead. */
  auth: ProviderAuthMethod;
  /**
   * The methods usable on this server, in preference order (ADR 0109):
   * `["oauth2","app_password"]` for Gmail with Google sign-in set up. Absent
   * from a backend that predates ADR 0109, which means app passwords only.
   */
  authMethods?: ProviderAuthMethod[];
  /** Set when `oauth2` is usable: whose sign-in page the user is sent to. */
  oauthIssuer?: OAuthIssuer;
  /** False when this server cannot connect it (Outlook without a Microsoft app registration). */
  available: boolean;
  /** Why not, in a sentence, when `available` is false. */
  unavailableReason?: string;
  /** Where the user creates the app password. */
  helpUrl: string;
  imapHost: string;
  smtpHost: string;
  /** Address domains that suggest this provider. A suggestion only: any address works with any provider. */
  domains: string[];
}

/** `GET /accounts/providers`. `enabled: false` (or an older backend) means no external providers at all. */
export interface ProvidersResponse {
  enabled: boolean;
  providers: ExternalProvider[];
}

/** `accountLimits` in `backend/internal/httpapi/nextcloud_wire.go`. */
export interface AccountLimits {
  /** The largest single attachment. */
  maxAttachmentBytes: number;
  /** All of one message's attachments together. */
  maxAttachmentTotalBytes: number;
  maxAttachments: number;
}

export interface AccountCapabilities {
  sieve: boolean;
  /**
   * Storage/message-count figures can be read for this mailbox.
   *
   * Poste-backed, and gated by the same per-host service credential as
   * `sieve` (ADR 0008). False means nobody can know this mailbox's usage —
   * the endpoint answers `501 unsupported_by_host` — so the UI says that
   * rather than requesting figures it already knows will not arrive.
   */
  quota: boolean;
  /**
   * The mailbox password can be changed from here. Poste-backed, like `quota`;
   * false means the endpoint would answer `501 unsupported_by_host`.
   */
  passwordChange: boolean;
  calendar: boolean;
  contacts: boolean;
  push: boolean;
}

/** Poste `GET /api/v1/boxes/{email}/quota`, relayed by our backend. */
export interface Quota {
  storageLimit: number;
  storageUsage: number;
  countLimit: number;
  countUsage: number;
}

/** PATCH /accounts/{id} — the only two mutable fields (doc 10 §5.2). */
export interface AccountUpdate {
  displayName?: string;
  color?: string;
}

/**
 * POST /accounts — attach a connected (secondary) mailbox.
 *
 * There is deliberately no host or port here. The server resolves the mail
 * host from the admin routing table by the address's domain, exactly as it
 * does for sign-in, and refuses any other field with `400`: letting a user
 * name a host would let them point the server at an arbitrary address (SSRF by
 * another name). A mailbox on a host the deployment has no routing rule for is
 * `403 domain_not_allowed`.
 */
export interface CreateAccountRequest {
  email: string;
  password: string;
  displayName?: string;
  color?: string;
  /**
   * A provider catalogue id (ADR 0107): the host comes from the catalogue,
   * not the routing table. Omitted means "our mail server", exactly as before.
   */
  provider?: string;
}

/**
 * `POST /accounts/oauth/start` (ADR 0109). `accountId` reconnects that
 * mailbox; without it a new one is connected, its address the one the issuer
 * signs in, never typed here.
 */
export interface OAuthStartRequest {
  provider: string;
  accountId?: string;
  displayName?: string;
  color?: string;
}

/** The issuer's sign-in page, to open as a full-page navigation (not a popup). */
export interface OAuthStartResponse {
  authorizeUrl: string;
}

/* ----------------------------------------------------------------- folders */

export type FolderRole =
  | 'inbox'
  | 'drafts'
  | 'sent'
  | 'archive'
  | 'junk'
  | 'trash'
  /** The top-level `Scheduled` folder: copies waiting to be sent later (ADR 0076). */
  | 'scheduled'
  /** The top-level `Snoozed` folder: messages away until they wake (ADR 0076). */
  | 'snoozed'
  | 'custom';

/** Where {@link Folder.role} came from, so the UI can explain a surprising one. */
export type FolderRoleSource = 'special-use' | 'heuristic' | 'user';

/**
 * The roles a person may point at a folder of their choosing (#68, ADR 0105).
 * INBOX is fixed by name, and Scheduled / Snoozed are the app's own folders.
 */
export type ChoosableFolderRole = Extract<FolderRole, 'archive' | 'sent' | 'drafts' | 'trash' | 'junk'>;

/** One role's entry in `GET /accounts/{id}/folder-roles`. */
export interface FolderRoleChoice {
  role: ChoosableFolderRole;
  /**
   * The folder the person chose, or `null` for Automatic. Still set when the
   * choice is {@link stale}: it is the id of the folder that went away.
   */
  folderId: string | null;
  /**
   * A choice exists but its folder no longer does — renamed or deleted in
   * another app. The role then resolves as if there were no choice.
   */
  stale: boolean;
  /** What Automatic resolves to, ignoring the choice; `null` when nothing. */
  automaticFolderId: string | null;
}

/** `GET`/`PUT /accounts/{id}/folder-roles`: always the five, in a fixed order. */
export interface FolderRoleChoices {
  roles: FolderRoleChoice[];
}

export interface Folder {
  /**
   * `base64url(raw mailbox name)`, unpadded — opaque. Never split on a
   * delimiter, never constructed client-side: the raw name is whatever the
   * server reported in `LIST` (modified UTF-7 unless `UTF8=ACCEPT`), and only
   * the round-tripped id addresses it correctly.
   */
  id: string;
  accountId: string;
  /** Decoded display form of the mailbox name, e.g. "INBOX/Projects/Acme". */
  path: string;
  name: string;
  parentId: string | null;
  role: FolderRole;
  /**
   * How {@link role} was decided; absent on a `custom` folder, which has no
   * provenance. `'user'` means the person chose it in Settings (#68).
   */
  roleSource?: FolderRoleSource;
  /** IMAP hierarchy delimiter, **per folder**: servers differ, and so can mailboxes. */
  delimiter: string;
  unreadCount: number;
  totalCount: number;
  /** IMAP \Noselect — a container that holds children but no messages. */
  selectable: boolean;
  subscribed: boolean;
  /** IMAP UIDVALIDITY; a change invalidates every cached id under this folder. */
  uidValidity: number;
  /**
   * CLIENT-ONLY. The wire sends a flat `Folder[]` and the client nests it from
   * `parentId`; nothing on the backend ever populates this.
   */
  children?: Folder[];
}

/**
 * Outcome of `POST /folders/{id}/mark-read`.
 *
 * `partial` is the one thing a bare count cannot say. Marking a big mailbox
 * read is one `UID SEARCH` and many `UID STORE`s, IMAP has no transaction
 * across them, and a run that stops half way really did flag everything up to
 * that point — so the server answers `200` with what it managed, and this flag
 * tells the UI to say "some are still unread" rather than "done".
 */
export interface MarkFolderReadResult {
  updatedCount: number;
  partial?: boolean;
}

/**
 * What emptying a folder does, decided by the SERVER from the folder's role:
 * `'permanent'` in Trash and Junk (flag `\Deleted` + `UID EXPUNGE` — no undo
 * anywhere), `'trash'` everywhere else (every message moved to Trash).
 */
export type EmptyFolderMode = 'trash' | 'permanent';

/**
 * The confirmation `POST /folders/{id}/empty` needs: exactly what its own
 * `409 confirm_empty` refusal reported, echoed back. `uidValidity` and
 * `uidNext` are the fresh `SELECT` the count came from, so the server touches
 * only the messages that were counted — mail that arrives after the person
 * said yes is left alone — and `mode` is what the person was told would
 * happen, so a confirmation for one mode can never run the other.
 */
export interface EmptyFolderConfirmation {
  uidValidity: number;
  uidNext: number;
  mode: EmptyFolderMode;
}

/**
 * Outcome of a confirmed `POST /folders/{id}/empty` (or of an unconfirmed one
 * on a folder that was already empty). `partial` means what it means for
 * mark-read: the run stopped part way, and `emptiedCount` is what it did.
 */
export interface EmptyFolderResult {
  emptiedCount: number;
  mode: EmptyFolderMode;
  partial?: boolean;
}

/* ---------------------------------------------------------------- messages */

/**
 * `snooze_returned` is the `$SnoozeReturned` keyword a woken snooze leaves on
 * the message (ADR 0076 §13): the list's "Returned from snooze" chip.
 */
export type MessageFlag =
  | 'seen'
  | 'flagged'
  | 'answered'
  | 'forwarded'
  | 'draft'
  | 'deleted'
  | 'snooze_returned';

export interface Address {
  name?: string;
  email: string;
}

/** Row shape for the virtualized list — deliberately light. */
export interface MessageSummary {
  id: string;
  accountId: string;
  folderId: string;
  threadId: string;
  subject: string;
  from: Address;
  to: Address[];
  date: ISODateTime;
  snippet: string;
  flags: MessageFlag[];
  hasAttachments: boolean;
  /** Number of messages in the thread, when the backend groups by thread. */
  threadCount?: number;
  sizeBytes: number;
  /**
   * The message's label keywords (plan/17 §4): its IMAP keywords that start
   * `$label_` or `$label=`, spelled as the server stores them. Always sent by
   * the server; optional here only because rows cached or persisted before
   * labels existed, and rows built locally, have none — read it as `[]`.
   */
  labelKeywords?: string[];
}

/* ----------------------------------------------------------- conversations */

/**
 * Conversation view (ADR 0077, docs/plan/16-conversation-view.md). A
 * conversation is the messages of ONE folder of ONE account that the server
 * threads together, plus the Sent messages merged into it. It never spans
 * accounts.
 */
export interface ConversationParticipant {
  name?: string;
  email: string;
  /** The account's own address, or the sender of a merged Sent message: shown as "me". */
  isMe: boolean;
  /** This participant has an unread message in the conversation (rendered bold). */
  unread: boolean;
}

/** Another folder a conversation spans — today only Sent. */
export interface ConversationLabel {
  folderId: string;
  name: string;
  role: string;
}

export interface ConversationSummary {
  /** Opaque; minted from the oldest member, so it survives new mail joining. */
  id: string;
  accountId: string;
  folderId: string;
  /** The oldest member's subject. */
  subject: string;
  /** The newest member's preview. */
  snippet: string;
  /** The newest member's displayed date: the sort key. */
  date: ISODateTime;
  /** The newest member: what opening, replying and Add to Tasks target. */
  newestMessageId: string;
  /** Every member IN THIS FOLDER, oldest first (capped at 1000, newest kept). */
  messageIds: string[];
  /** Members plus merged Sent messages. */
  messageCount: number;
  unreadCount: number;
  /** `seen` iff every member is seen; `flagged` / `answered` iff any is. */
  flags: MessageFlag[];
  hasAttachments: boolean;
  /** Senders in chronological order, deduped, at most 8. */
  participants: ConversationParticipant[];
  labels: ConversationLabel[];
  /**
   * Label keywords of every message in the conversation, merged Sent members
   * included, de-duplicated case-insensitively (plan/17 §4). Unrelated to
   * `labels`, which are the folder chips.
   */
  labelKeywords?: string[];
  /** Ids of the merged Sent members, which a label verb also has to reach. */
  sentMessageIds?: string[];
}

export type ConversationSortOrder = 'date-desc' | 'date-asc';

export interface ConversationPage extends Paginated<ConversationSummary> {
  /** The folder generation this page was built from: hand it to `sync`. */
  state: string;
  /** How the server grouped: its THREAD=REFERENCES, THREAD=REFS, or our own fallback. */
  threading: 'references' | 'refs' | 'local';
}

export interface ConversationListQuery extends PageQuery {
  accountId: string;
  folderId: string;
  sort?: ConversationSortOrder;
  unreadOnly?: boolean;
  flaggedOnly?: boolean;
}

/** One row the client holds, as `sync` needs to judge whether it changed. */
export interface ConversationSyncRow {
  id: string;
  newestMessageId: string;
  messageCount: number;
}

export interface ConversationSyncRequest {
  state: string;
  /** At most 500. */
  rows: ConversationSyncRow[];
  unreadOnly?: boolean;
  flaggedOnly?: boolean;
}

export interface ConversationSyncResult {
  state: string;
  /** Insert or replace, by id; place by `date`. */
  upserts: ConversationSummary[];
  /** Ids the client sent that no longer exist. */
  removed: string[];
  /** Cannot be answered by patching: refetch the loaded pages in place. */
  reset: boolean;
}

/** A message in the reading pane's conversation stack. */
export interface ConversationMessage extends MessageSummary {
  /** False for a merged Sent message: it lives in another folder (its own `folderId`). */
  inFolder: boolean;
}

export interface ConversationDetail {
  conversation: ConversationSummary;
  /** Oldest first: members and merged Sent messages. */
  messages: ConversationMessage[];
  /** Older messages not included (the newest 100 are). */
  omittedCount: number;
}

export interface MessageDetail extends MessageSummary {
  cc: Address[];
  bcc: Address[];
  replyTo: Address[];
  /** Sanitized HTML body, when present. */
  bodyHtml?: string;
  /**
   * The sender's stylesheet after the server's CSS pass (ADR 0081): rewritten,
   * scoped under the frame's message root. Present only beside `bodyHtml`,
   * and only ever given to the message frame as a stylesheet.
   */
  bodyCss?: string;
  /** The sender styled this message for a dark canvas themselves (ADR 0081). */
  bodyDarkReady?: boolean;
  /**
   * The server's sanitizer version (`mail.SanitizerVersion`) that made
   * `bodyHtml`. The offline cache drops a body made under another one.
   */
  renderVersion?: number;
  bodyText?: string;
  attachments: Attachment[];
  /**
   * RFC 5322 headers, **ordered, duplicates preserved**.
   *
   * An array of pairs rather than a map: `Received` legitimately appears many
   * times, and a map keyed on protocol data would also put user-controlled
   * strings through the wire-case converter (ADR 0009).
   */
  headers: MessageHeader[];
  /** Present when the message carries an iTIP invite (CalDAV integration). */
  invite?: CalendarInvite;
  /** What the sanitizer stripped, so the reading pane can offer "show images". */
  sanitizeReport?: SanitizeReport;
  /**
   * Remote-content accounting for this render (ADR 0018 §6, plan/12 §1.7).
   *
   * Absent on a backend that predates the proxy, which is why the reading pane
   * still falls back to `sanitizeReport.remoteImagesBlocked` for the count.
   */
  remoteContent?: RemoteContentInfo;
  /** True when the body was cut short because the source was very large. */
  truncated?: boolean;
  /**
   * How this message says it can be unsubscribed from (RFC 2369 / RFC 8058),
   * already chosen and sanitized by the server. Absent when it carries no
   * `List-Unsubscribe` the server is willing to act on.
   */
  unsubscribe?: MessageUnsubscribe;
  /**
   * How faithfully the body is shown (#42). `text_only`: the sender's HTML
   * was not shown safely, so this is text. `unreadable`: neither HTML nor
   * text could be read. `degraded`: a part was cut short or came back
   * missing. Absent (an older server, an offline-cached body) means `ok`.
   */
  renderStatus?: 'ok' | 'degraded' | 'text_only' | 'unreadable';
}

/**
 * The one unsubscribe route the server picked for a message.
 *
 * - `one_click` — RFC 8058: the SERVER posts to `target` (always https) when
 *   asked to (`POST …/unsubscribe`). The client never fetches it.
 * - `mailto` — `target` is the bare address; `subject` / `body` are the
 *   sanitized hfields of the `mailto:` URI. The user sends it themselves.
 * - `link` — `target` is an http(s) page the user opens in a new tab.
 */
export interface MessageUnsubscribe {
  method: 'one_click' | 'mailto' | 'link';
  target: string;
  subject?: string;
  body?: string;
}

/** `200` from `POST …/messages/{id}/unsubscribe`: the list host accepted it. */
export interface UnsubscribeResult {
  status: 'done';
  /** The host the one-click request went to, for the confirmation line. */
  host: string;
}

export interface MessageHeader {
  name: string;
  value: string;
}

/**
 * One method's result out of the Authentication-Results header **the
 * receiving server stamped** (#326, ADR 0062).
 *
 * `result` is the empty string when that header recorded nothing for this
 * method. That is not a pass and not a fail: the UI says "your mail server
 * did not record a check". There is no client-side default that could turn
 * silence into reassurance.
 */
export interface AuthMethodResult {
  result: string;
  domain?: string;
  detail?: string;
}

/**
 * A difference between two headers, stated as a fact.
 *
 * Deliberately not a verdict: a newsletter legitimately has a `Reply-To`
 * elsewhere and an ESP legitimately issues `Message-ID`s at its own domain.
 * The wording lives in the panel; what crosses the wire is the fact.
 */
export interface AuthObservation {
  kind: 'display_name_address' | 'reply_to_domain' | 'return_path_domain' | 'message_id_domain';
  from?: string;
  found?: string;
}

/**
 * The advisory SPF lookup: the From domain's record **as published right
 * now**, evaluated against the IP our own server recorded.
 *
 * `outcome` is an RFC 7208 result. `temperror` and `permerror` mean "could
 * not check" and are never rendered as a fail.
 */
export interface SpfCheck {
  checked: boolean;
  outcome?: string;
  domain?: string;
  record?: string;
  mechanism?: string;
  clientIp?: string;
  lookups?: number;
  reason?: string;
  envelopeFrom?: string;
  /**
   * The check fell back to the From domain because no envelope sender was
   * recorded. That is not the question SPF answers, so the panel says so.
   */
  usedFromDomain?: boolean;
}

/** GET /accounts/{id}/messages/{messageId}/authentication. */
export interface MessageAuthentication {
  /** The host whose stamp is read. Empty when there is no such host. */
  authservId?: string;
  /** Whether a header stamped by that host was found at all. */
  trusted: boolean;
  /** That header, verbatim. */
  raw?: string;
  /** How many Authentication-Results headers someone else wrote were discarded. */
  ignoredHeaders: number;
  spf: AuthMethodResult;
  dkim: AuthMethodResult;
  dmarc: AuthMethodResult;
  /**
   * The host's record that a signed-in user of it submitted the message. It
   * stamps nothing else on such mail, so this is empty exactly when the three
   * above are not.
   */
  auth?: AuthMethodResult;
  fromAddress?: string;
  fromDomain?: string;
  spfCheck: SpfCheck;
  observations: AuthObservation[];
}

export interface SanitizeReport {
  remoteImagesBlocked: number;
  scriptsRemoved: number;
  formsRemoved: number;
}

/**
 * Result of a bulk message mutation (flags, move, copy, archive, delete).
 *
 * Always HTTP 200, even when every item failed: partial success is the normal
 * case over IMAP, so the per-item outcome is the body, not the status. An
 * empty `messageIds` is a `400`.
 */
export interface BulkResult {
  /** `newId` is present per item when the server supports `UIDPLUS`. */
  applied: { id: string; newId?: string }[];
  failed: { id: string; code: ApiErrorCodeLike; message: string }[];
}

export interface Attachment {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  inline: boolean;
  contentId?: string;
  /** Backend-relative download path. */
  downloadUrl: string;
  /**
   * An attached email (ADR 0092): `message/rfc822`, `message/global`, or a
   * file named `*.eml` -- decided by the server from the declared type and
   * the name only.
   */
  attachedMessage?: boolean;
  /**
   * What opens it: `GET …/messages/{id}/attached/{attachedPath}`. Absent on
   * an attached email nested past the server's depth cap, which is then
   * download-only.
   */
  attachedPath?: string;
}

/**
 * `GET /accounts/{id}/messages/{messageId}/attached/{path}` (ADR 0092): an
 * email attached to a message, read and sanitized by the server exactly as a
 * message is. `path` is part ids joined by `-`, outermost first.
 *
 * `renderStatus` says how far it could be read: `ok`; `degraded` (its MIME
 * structure was broken and recovered, so it may be incomplete); `text_only`
 * (no email headers at all -- the bytes are shown as text); `unreadable` (not
 * an email: nothing to show, offer the download).
 */
export interface AttachedMessage {
  path: string;
  renderStatus: 'ok' | 'degraded' | 'text_only' | 'unreadable';
  /** What the attached email itself downloads as. */
  filename: string;
  /** Downloads the attached email itself (`.eml`). */
  downloadUrl: string;
  sizeBytes: number;
  subject: string;
  from: Address[];
  to: Address[];
  cc: Address[];
  replyTo: Address[];
  /** Absent when it carries no Date header that parses. */
  date?: ISODateTime;
  /** Its Message-ID header, without angle brackets. */
  rfcMessageId?: string;
  bodyHtml?: string;
  bodyText?: string;
  bodyCss?: string;
  bodyDarkReady?: boolean;
  renderVersion?: number;
  truncated?: boolean;
  attachments: Attachment[];
  headers: MessageHeader[];
  sanitizeReport?: SanitizeReport;
  remoteContent?: RemoteContentInfo;
}

/**
 * A file uploaded ahead of the send and waiting to be named in
 * `DraftInput.attachmentIds` (doc 10 §5.5).
 *
 * `filename` and `contentType` are the SERVER's answers, not what the browser
 * offered: the backend sanitizes the name and derives the type from the bytes,
 * so this is what the recipient will actually see.
 */
export interface StagedAttachment {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  inline: boolean;
  contentId?: string;
}

/**
 * One complete message order: a field and a direction.
 *
 * `arrival` is the server's own receipt time (IMAP INTERNALDATE, which for one
 * UIDVALIDITY is UID order) and is the only order every IMAP server can do.
 * `date` is the `Date:` HEADER -- the value the list's own date column shows --
 * and, like the rest, needs the server to support the SORT extension. Which
 * orders a given account can actually use comes back on the page itself, in
 * `Paginated.sortsAvailable`; nothing here should be offered without checking.
 */
export type MessageSortOrder =
  | 'arrival-desc'
  | 'arrival-asc'
  | 'date-desc'
  | 'date-asc'
  | 'from-asc'
  | 'from-desc'
  | 'subject-asc'
  | 'subject-desc'
  | 'size-desc'
  | 'size-asc';

/** The order a list is in when nobody has chosen one: newest arrival first. */
export const DEFAULT_MESSAGE_SORT: MessageSortOrder = 'arrival-desc';

/** Menu labels. Both dates are spelled out, because they are different facts. */
export const MESSAGE_SORT_LABELS: Record<MessageSortOrder, string> = {
  'arrival-desc': 'Newest first (received)',
  'arrival-asc': 'Oldest first (received)',
  'date-desc': 'Newest first (sent)',
  'date-asc': 'Oldest first (sent)',
  'from-asc': 'Sender A–Z',
  'from-desc': 'Sender Z–A',
  'subject-asc': 'Subject A–Z',
  'subject-desc': 'Subject Z–A',
  'size-desc': 'Largest first',
  'size-asc': 'Smallest first',
};

/**
 * The same orders, short enough for a phone's one toolbar row. A select shows
 * its current option's text and cannot be told to show anything else, so the
 * handheld list offers these instead of letting "Newest first (received)"
 * clip mid-word. Received stays the unmarked default and sent keeps its tag,
 * so the two dates are still told apart.
 */
export const MESSAGE_SORT_SHORT_LABELS: Record<MessageSortOrder, string> = {
  'arrival-desc': 'Newest',
  'arrival-asc': 'Oldest',
  'date-desc': 'Newest (sent)',
  'date-asc': 'Oldest (sent)',
  'from-asc': 'Sender A–Z',
  'from-desc': 'Sender Z–A',
  'subject-asc': 'Subject A–Z',
  'subject-desc': 'Subject Z–A',
  'size-desc': 'Largest',
  'size-asc': 'Smallest',
};

export interface MessageListQuery extends PageQuery {
  accountId: string;
  folderId: string;
  /** IMAP SEARCH-backed free-text query; empty means "everything in folder". */
  search?: string;
  unreadOnly?: boolean;
  flaggedOnly?: boolean;
  sort?: MessageSortOrder;
  /** Group by thread (`THREAD`, else `References`). */
  threaded?: boolean;
}

/**
 * Where a search looks (GET /accounts/{id}/search `scope`).
 *
 * `folder` is the one folder named by `folderId` — the same answer the folder
 * listing gives with `search`. `all` is every selectable folder of the
 * account, with Trash and Junk left out unless `includeTrashJunk` is set
 * (Gmail's default "search all mail", and its `in:anywhere`).
 */
export type SearchScope = 'folder' | 'all';

/** The input to `MailApi.messages.search`. */
export interface MessageSearchQuery extends PageQuery {
  query: string;
  /** Scope `folder` only: the folder searched. The server defaults to INBOX. */
  folderId?: string;
  /** Absent means `folder`, which is what the endpoint always did. */
  scope?: SearchScope;
  /** Scope `all` only: search Trash and Junk too. */
  includeTrashJunk?: boolean;
  sort?: MessageSortOrder;
}

/**
 * The orders a search across folders can be served in.
 *
 * A cross-folder page is one merge of many per-folder answers, and the merge
 * compares a value every row carries and that means the same in every folder:
 * the two dates and the size. A sender or subject order would have to repeat
 * the server's own RFC 5256 collation here, so the backend refuses it — the
 * client clamps to `date-desc` rather than sending a request that 400s.
 */
export const CROSS_FOLDER_SORTS: readonly MessageSortOrder[] = [
  'date-desc',
  'date-asc',
  'arrival-desc',
  'arrival-asc',
  'size-desc',
  'size-asc',
];

/** What a cross-folder search is ordered by when nobody chose: the `Date:` column. */
export const DEFAULT_CROSS_FOLDER_SORT: MessageSortOrder = 'date-desc';

/**
 * One page of search results.
 *
 * The folder facts are what makes a partial answer honest: a search of twenty
 * folders in which three timed out is not "no more results", and the list says
 * which three.
 */
export interface SearchPage extends Paginated<MessageSummary> {
  /** `in` when the query's own `in:` terms chose the folders. */
  scope?: SearchScope | 'in';
  /** Folder ids that were searched. */
  foldersSearched?: string[];
  /** Folder ids that did not answer within their time box. */
  foldersTimedOut?: string[];
  /** Folder ids that failed outright. */
  foldersFailed?: string[];
  /** Folder ids left out by default (Trash and Junk). */
  foldersExcluded?: string[];
}

export interface FlagUpdate {
  add?: MessageFlag[];
  remove?: MessageFlag[];
}

export interface MoveRequest {
  messageIds: string[];
  targetFolderId: string;
}

/**
 * `POST /accounts/{id}/messages/transfer` (ADR 0108): move or copy messages
 * into a folder of ANOTHER of the owner's mailboxes. At most
 * `MAX_TRANSFER_MESSAGES` ids; the same account is refused (`same_account`).
 */
export interface TransferRequest {
  messageIds: string[];
  targetAccountId: string;
  targetFolderId: string;
  mode: TransferMode;
}

export type TransferMode = 'move' | 'copy';

/**
 * One message's outcome. `already_there`: the target folder already held a
 * message with this Message-ID (an earlier or retried transfer), so nothing
 * was appended; on a move the source copy is still removed.
 * `copied_not_removed`: a move whose source server has no UIDPLUS, so the
 * original is only marked deleted.
 */
export type TransferStatus = 'moved' | 'copied' | 'already_there' | 'copied_not_removed' | 'failed';

export interface TransferItemResult {
  id: string;
  status: TransferStatus;
  /** The message's id in the target folder, when the target server said. */
  targetId?: string;
  error?: { code: ApiErrorCodeLike; message: string };
}

export interface TransferResult {
  results: TransferItemResult[];
}

/* ----------------------------------------------------------- compose/send */

export interface Draft {
  id: string;
  accountId: string;
  /** Identity (From address) this draft will be sent as. */
  fromAddress: string;
  to: Address[];
  cc: Address[];
  bcc: Address[];
  subject: string;
  bodyHtml: string;
  bodyText?: string;
  attachmentIds: string[];
  inReplyToMessageId?: string;
  /**
   * The message a forward carries, with `mode: 'forward'` only. Its own field
   * rather than a second meaning of `inReplyToMessageId`, because that one
   * THREADS: a forward that lost its mode on the way would otherwise go out as
   * a reply. The server uses this only to mark the original `$Forwarded`, and
   * refuses it on any other mode (and a forward that also names a reply
   * parent).
   */
  forwardedMessageId?: string;
  /**
   * The Nextcloud link shares whose block is in this message's body (ADR
   * 0084 D13). Owner-checked by the server; an unknown id is a 404. Send and
   * Send later mark the rows sent; a draft save only keeps them from being
   * swept as orphans.
   */
  nextcloudUploadIds?: string[];
  /** 'reply' | 'reply-all' | 'forward' — drives quoting behaviour. */
  mode?: 'new' | 'reply' | 'reply-all' | 'forward';
  updatedAt: ISODateTime;
  /**
   * What `attachmentIds` point at. Present on a draft READ back from the
   * server, which re-stages the files out of the stored message under fresh
   * ids; a composer that only had the ids would have nothing to render in the
   * attachment strip. Absent on the echo of a write, where the client already
   * knows — it uploaded them.
   */
  attachments?: StagedAttachment[];
  /**
   * Something that did not go to plan and did not cost the draft: an older
   * copy the server could not remove, a file it could not reopen. Shown to the
   * user verbatim. Never set when the draft itself failed to save.
   */
  warning?: string;
  /**
   * The draft's identity across saves (ADR 0100): the `X-Webmail-Draft-Id`
   * every version carries, while `id` changes on every save. A composer mints
   * it before its first save and adopts whatever the server echoes; the
   * stored draft's own identity always wins. Absent on a draft saved by an
   * older build or another client, until its next save stamps one.
   */
  draftUuid?: string;
}

/**
 * The body of a draft write or a direct send.
 *
 * `accountId` is not a member: the account is in the path
 * (`/accounts/{id}/drafts`, `/accounts/{id}/send`), so carrying it in the body
 * too would create two sources of truth that can disagree. `attachments` and
 * `warning` are not members either: both are things the server says about a
 * draft, not things a client may assert about one.
 */
export type DraftInput = Omit<
  Draft,
  'id' | 'accountId' | 'updatedAt' | 'attachments' | 'warning'
> & {
  /**
   * On a save over an existing draft: the `updatedAt` of the version the
   * composer edited from. The server uses it only to recognise that version
   * after the Drafts folder was rebuilt (ADR 0100); a version replaced or
   * removed elsewhere is refused with 409 `draft_changed` either way.
   */
  baseUpdatedAt?: ISODateTime;
};

/**
 * The body of a direct send: the message, plus the draft it was composed in.
 *
 * `draftId` is how the send retires that draft. The server removes it once the
 * message is accepted and leaves it alone when the send fails, so a message
 * that was autosaved and then sent never survives in Drafts -- including from
 * a popped-out window, which closes itself before a follow-up DELETE of its
 * own could land.
 */
export type SendInput = DraftInput & { draftId?: string };

/**
 * What `GET /compose/reply` answers: a `DraftInput`, plus what its
 * `attachmentIds` point at.
 *
 * A forward's ids are the ORIGINAL's files, which the server copied into the
 * staging store; the composer never uploaded them and has nothing to render
 * the attachment strip from without this. Absent when there are none.
 */
export type ComposePrefill = DraftInput & { attachments?: StagedAttachment[] };

export interface SendResult {
  /** The `Message-ID` header of what was sent, when the host reports one. */
  messageId?: string;
  /** Folder the copy was filed into (usually Sent). */
  savedToFolderId?: string;
  savedToSent: boolean;
  queuedAt: ISODateTime;
  /** Recipients the MTA accepted. */
  accepted: string[];
  /** Recipients it refused, with the SMTP reply. */
  rejected: RejectedRecipient[];
  /** Sent, but with a caveat worth surfacing (e.g. the Sent copy failed). */
  warning?: string;
  /** UID of the copy in Sent (UIDPLUS `APPENDUID`, or found by its Message-ID). */
  sentUid?: number;
  /**
   * The app id of the copy in Sent. With `savedToFolderId` it opens at the
   * ordinary mail route, which is what "View message" after a send does.
   */
  sentMessageId?: string;
  /**
   * True when the server deliberately did NOT save a copy, because this
   * mailbox's server files sent mail itself (Gmail) or the person turned the
   * copy off (`SentSettings.saveSentCopy`). `savedToSent` is false then and it
   * is not a fault: nothing should be said about it.
   */
  sentFiledByServer?: boolean;
  /**
   * Present when the copy to Sent failed and the server kept the message to
   * try again (#84). `warning` is then the server's own sentence about it.
   */
  sentCopy?: SentCopy;
}

/**
 * Where a kept copy of sent mail stands (#84). `pending` waits for its next
 * try, `saving` is being appended now, and `failed` means the automatic
 * retries gave up and the person has to choose: save it, download it, or
 * dismiss it.
 */
export type SentCopyState = 'pending' | 'saving' | 'failed';

/** Why a sent copy is still open; `error` is the sentence to show. */
export type SentCopyErrorCode =
  | 'temporary'
  | 'retries_exhausted'
  | 'no_sent_folder'
  | 'too_large'
  | 'credentials_rejected'
  | 'rejected';

/**
 * A message that was SENT but whose copy is not in the Sent folder yet (#84).
 * The server keeps the message, sealed, and appends it again with backoff;
 * nothing here ever goes back through SMTP.
 */
export interface SentCopy {
  id: string;
  accountId: string;
  state: SentCopyState;
  attempts: number;
  /** When the next automatic try is due; `pending` only. */
  nextAttemptAt?: ISODateTime;
  /**
   * True when the server keeps trying with the app closed (the mailbox has a
   * background credential). False: it tries only while someone is signed in
   * and asks (`POST …/sent-copies/run`).
   */
  unattended: boolean;
  errorCode?: SentCopyErrorCode;
  /** The server's own sentence, safe to show. */
  error?: string;
  subject: string;
  /** Every recipient address (To, Cc and Bcc). */
  to: string[];
  sentAt: ISODateTime;
  /** When the kept copy is deleted for good. */
  expiresAt: ISODateTime;
}

/** `GET /accounts/{id}/sent-copies`: the open ones only. */
export interface SentCopyList {
  items: SentCopy[];
}

/** A kept copy that is now in Sent, with what "View message" needs. */
export interface SavedSentCopy {
  id: string;
  savedToFolderId: string;
  sentUid?: number;
  sentMessageId?: string;
}

/** `POST /accounts/{id}/sent-copies/run`: what is still open, and what was saved. */
export interface SentCopyRunResult {
  items: SentCopy[];
  saved: SavedSentCopy[];
}

/** `GET`/`PUT /accounts/{id}/sent-settings`. */
export interface SentSettings {
  /** Save a copy of sent mail to Sent. Defaults to true. */
  saveSentCopy: boolean;
  /**
   * The server detected that this mail server files sent mail itself (today:
   * Gmail's `X-GM-EXT-1`), so the app never adds a copy, whatever the switch.
   */
  serverFilesSent: boolean;
  serverFilesReason?: 'gmail';
}

export interface RejectedRecipient {
  address: string;
  /** SMTP reply code as a string, e.g. "550". */
  code: string;
  message: string;
}

/* ------------------------------------------------------------------ sieve */

/**
 * The mailbox's Sieve state — **one script per mailbox** (doc 05 §11.1).
 *
 * `source` says whether the builder may own it: `managed` (we wrote it),
 * `imported` (someone else wrote it — a hosting panel, a hand edit — and a save
 * edits it in place; what the model cannot describe is listed as `locked`
 * rules), `diverged` (we wrote it and someone edited it since) or `foreign`
 * (it cannot even be split into statements). `rules` is `null` when `source`
 * is `foreign` — the raw editor is the only safe surface, and writing `rules`
 * over it answers `409 conflict` with `details.reason: "foreign_script"` (G6).
 *
 * The `null` / `[]` distinction on `rules` is load-bearing in both directions:
 * `null` is "these filters cannot be described here", `[]` is "there are no
 * filters". Rendering the first as the second offers an empty builder over a
 * script that a save would then delete.
 */
export interface SieveState {
  script: SieveScript | null;
  source: 'managed' | 'imported' | 'diverged' | 'foreign';
  rules: SieveRule[] | null;
  /** Why `source` is what it is, when that needs explaining. */
  reason?: string;
  /**
   * The out-of-office card, as read from its own block in the same script
   * (`null` = never configured). It is not one of `rules`: the vacation block
   * sits above every rule and is edited in Settings → Out of office, so the
   * Filters page only points at it. Optional here because older fixtures and
   * the mock predate it; the backend always sends it.
   */
  vacation?: VacationSettings | null;
  /**
   * sha256 of the script this state describes. Sent back as `baseHash` on a
   * save, so a save from a page that read an older script — before a hosting
   * panel (WHP) or another tab rewrote it — is refused with `409
   * script_changed` instead of undoing that change. Optional: the mock does
   * not compute one.
   */
  scriptHash?: string;
  /**
   * `diverged` only: what saving the rules listed here would write, for the
   * "View differences" against `script` (#138). Absent for every other state,
   * and from an older server.
   */
  proposedScript?: string;
}

/**
 * Settings → Out of office: one mailbox's automatic reply.
 *
 * The backend compiles it into its own marked block at the TOP of the
 * mailbox's single Sieve script (a Sieve `vacation` bounded by `currentdate`
 * tests), so no filter rule with a `stop` can skip it, and the mail server
 * enforces the dates on its own — nothing here has to be open for it to start
 * or stop.
 */
export interface VacationSettings {
  enabled: boolean;
  /** First day, `YYYY-MM-DD`, in `timeZone`. */
  startDate: string;
  /** Last day inclusive, `YYYY-MM-DD`, or `''` for no last day. */
  endDate: string;
  /** IANA zone the two days are read in (Settings → General, or the device's). */
  timeZone: string;
  /** `''` lets the mail host use its own "Auto: <original subject>". */
  subject: string;
  /** Plain text; line breaks are kept. */
  body: string;
  /** At most one reply per sender every this many days (Sieve `:days`, 1–30). */
  days: number;
}

/**
 * `GET /accounts/{id}/sieve/vacation`.
 *
 * `state` is `none` when the script has no vacation block at all, `diverged`
 * when somebody edited inside it (a save then needs `allowOverwrite`), and
 * `managed` otherwise. `from` and `addresses` are decided by the server from
 * the mailbox itself — the client never picks who the reply claims to be.
 * `conflict`, when non-empty, names another auto-reply in the same script
 * (a filter rule or a hand-written command); only one may run per message, so
 * turning this on is refused while it exists.
 */
export interface VacationState {
  vacation: VacationSettings | null;
  state: 'managed' | 'diverged' | 'none';
  reason?: string;
  from: string;
  addresses: string[];
  conflict?: string;
}

/** `PUT /accounts/{id}/sieve/vacation`. */
export interface VacationUpdate {
  vacation: VacationSettings;
  /** Consent to replace a hand-edited vacation block (409 `diverged_vacation`). */
  allowOverwrite?: boolean;
}

/** Raw script as stored by the mail host. */
export interface SieveScript {
  name: string;
  active: boolean;
  script: string;
  /**
   * Optional because Poste does not report one: its endpoint answers
   * `{ script }` and nothing else, so a backend that filled this in would be
   * stamping the read time onto the script and calling it a modification date.
   */
  updatedAt?: ISODateTime;
}

/**
 * `PUT /accounts/{id}/sieve` takes exactly one of these.
 *
 * `allowOverwrite` is the explicit consent a `diverged` script needs before
 * the generated block replaces what someone edited by hand (doc 05 §9.2).
 * Without it that save answers `409 conflict` with
 * `details.reason: "diverged_script"`. There is no such flag for `foreign`:
 * consenting there would mean agreeing to delete filtering that was never
 * described to the user in the first place.
 */
export type SieveUpdate = (
  | { rules: SieveRule[]; allowOverwrite?: boolean }
  | { script: string }
) & {
  /**
   * `SieveState.scriptHash` of the state being edited. With it, a rule the
   * script has and the list leaves out is deleted (the list provably came
   * from this script); without it, it is kept.
   */
  baseHash?: string;
};

export type SieveConditionField = 'from' | 'to' | 'cc' | 'subject' | 'body' | 'header' | 'size';
export type SieveConditionOp =
  | 'contains'
  | 'is'
  | 'matches'
  | 'not-contains'
  | 'greater-than'
  | 'less-than';

export interface SieveCondition {
  field: SieveConditionField;
  /** Only meaningful when field === 'header'. */
  headerName?: string;
  op: SieveConditionOp;
  value: string;
}

export type SieveActionType =
  | 'fileinto'
  | 'redirect'
  | 'discard'
  | 'reject'
  | 'vacation'
  | 'setflag'
  /** Apply a label (plan/17 §7): `value` is the label's keyword. */
  | 'label'
  | 'stop';

export interface SieveAction {
  type: SieveActionType;
  /** fileinto → folder path; redirect → address; reject/vacation → message. */
  value?: string;
  /** vacation only. */
  days?: number;
  /** fileinto only: Sieve `:create` — make the folder on first delivery if it is missing. */
  create?: boolean;
  /** fileinto / redirect: Sieve `:copy` — also keep the message where it would have gone. */
  copy?: boolean;
  /**
   * setflag only: written as `setflag` (replaces flags set earlier in the same
   * delivery) rather than `addflag`. Set when the script said so; new flag
   * actions are `addflag`.
   */
  replace?: boolean;
}

/** The rule-builder's model; the backend compiles it to Sieve source. */
export interface SieveRule {
  id: string;
  name: string;
  enabled: boolean;
  match: 'all' | 'any';
  conditions: SieveCondition[];
  actions: SieveAction[];
  stopProcessing: boolean;
  order: number;
  /**
   * `script`: the rule is text of the mailbox's own script (a hosting panel's
   * or hand-written) and is edited in place — unchanged rules are written back
   * byte for byte. Absent: this app's managed block. Decided by the server.
   */
  origin?: 'script';
  /**
   * Sieve the builder cannot describe (an elsif chain, an envelope test…). It
   * is listed so its position is visible, may be moved, and is written back
   * exactly as it is; it cannot be edited, disabled or deleted here (#211).
   */
  locked?: boolean;
  /** A locked rule's text, exactly as the script has it. */
  raw?: string;
  /** Why a locked rule could not be read as a rule. */
  lockedReason?: string;
}

/**
 * Exactly one of `rules` (the builder's model) or `script` (raw Sieve, e.g.
 * the unsaved text of the source editor) says what to dry-run.
 */
export type SieveTestRequest = ({ rules: SieveRule[] } | { script: string }) & {
  /** Raw RFC822 sample, or a reference to an existing message. */
  sampleMessageId?: string;
  sampleRaw?: string;
};

export interface SieveTestResult {
  matchedRuleIds: string[];
  actions: SieveAction[];
  /** The compiled script that would run, for the raw-editor view. */
  compiled: string;
  /**
   * What the result does not show, in words to put under it: an auto-reply,
   * forward or bounce is listed in `actions`, but nothing was sent and its
   * delivery-time rules (vacation's :days) were not simulated (#140). The
   * server always sends a list; optional only so an older server reads as
   * "no notes".
   */
  notes?: string[];
}

/**
 * Why a copy of the script was kept. Each backup is the script as it was
 * BEFORE the write this names, so `block_sender` is the script from just
 * before a sender was blocked. A cause this client does not know yet (a newer
 * server) is kept as a string and reads as "a change".
 */
export type SieveBackupCause =
  | 'filters'
  | 'filters_script'
  | 'block_sender'
  | 'unblock_sender'
  | 'out_of_office'
  | 'label_rename'
  | 'label_delete'
  | 'restore'
  | 'folder_rename'
  | (string & {});

/** One kept copy of a mailbox's Sieve script (`GET /accounts/{id}/sieve/backups`). */
export interface SieveBackupMeta {
  /** Per-mailbox sequence number; larger is newer. */
  id: number;
  createdAt: ISODateTime;
  cause: SieveBackupCause;
  /** The blocked address, the label (its new name on a rename), the version restored… May be empty. */
  detail: string;
  /** The signed-in address that made the write. */
  actor: string;
  /** "Firefox on Linux". May be empty. */
  device: string;
  bytes: number;
  /** Hex sha256 of the script bytes — compared with `SieveState.scriptHash`. */
  sha256: string;
}

/** `GET /accounts/{id}/sieve/backups/{backupId}`: the metadata and the full text. */
export interface SieveBackup extends SieveBackupMeta {
  script: string;
}

/** How many copies the server keeps. */
export interface SieveBackupRetention {
  keepLatest: number;
  keepDays: number;
  maxKept: number;
}

/** `GET /accounts/{id}/sieve/backups`, newest first (may be empty). */
export interface SieveBackupList {
  /** The mailbox's address, so a message can name it. */
  mailbox: string;
  retention: SieveBackupRetention;
  backups: SieveBackupMeta[];
}

/** `POST /accounts/{id}/sieve/backups/{backupId}/restore`. */
export interface SieveRestoreRequest {
  /** `SieveState.scriptHash` from the latest read; a stale one is `409 script_changed`. */
  baseHash: string;
}

/* --------------------------------------------------------------- contacts */

export interface AddressBook {
  id: string;
  accountId: string;
  name: string;
  readOnly: boolean;
  /** CardDAV collection href. */
  href: string;
  /** The collection's CardDAV description, when the server stores one. */
  description?: string;
  /**
   * Absent more often than not: the backend omits it rather than guess,
   * because counting means reading the whole collection and a wrong count in
   * the sidebar is worse than none. Never render a badge for `undefined`.
   */
  contactCount?: number;
}

export interface Contact {
  id: string;
  addressBookId: string;
  displayName: string;
  firstName?: string;
  lastName?: string;
  organization?: string;
  title?: string;
  emails: ContactField[];
  phones: ContactField[];
  addresses: ContactAddress[];
  notes?: string;
  /** vCard `NICKNAME`. */
  nickname?: string;
  /** vCard `URL`s — websites, profiles. */
  urls?: ContactField[];
  /**
   * vCard `BDAY` / `ANNIVERSARY` exactly as stored: normally `YYYY-MM-DD`, but
   * another client may have written `19901231` or a year-less `--1231`, so it
   * is displayed and round-tripped as text, never parsed into a `Date`.
   */
  birthday?: string;
  anniversary?: string;
  photoUrl?: string;
  categories: string[];
  /** A group card (`KIND:group`). Read-only: the API cannot write one. */
  isGroup?: boolean;
  /** The `urn:uuid:` members of a group card. */
  members?: string[];
  /** CardDAV ETag, needed for conflict-safe writes (`If-Match`). */
  etag?: string;
  updatedAt: ISODateTime;
}

/**
 * One card's outcome in a vCard import (POST …/address-books/{id}/import,
 * #241). `reason` is a code to branch on (`duplicate`, `duplicate_in_file`,
 * `malformed`, `no_name`, `charset`, `photo_too_large`, `photo_unreadable`,
 * `unsupported_version`, `too_large`, `not_attempted`, `refused`); `message`
 * is the server's sentence for it.
 */
export interface ContactImportResult {
  /** 1-based position of the card in the file. */
  index: number;
  status: 'imported' | 'skipped' | 'failed';
  reason?: string;
  message?: string;
  displayName?: string;
  uid?: string;
  /** The card had no UID and the server minted one. */
  uidGenerated?: boolean;
  /** Set when `status` is `imported`. */
  contactId?: string;
}

/**
 * The import report. Partial success is still a 200 with this body: a card
 * whose UID the book already holds is `skipped` (never overwritten), and a
 * card that cannot be stored is `failed` with its reason.
 */
export interface ContactImportReport {
  total: number;
  imported: number;
  skipped: number;
  failed: number;
  results: ContactImportResult[];
}

export interface ContactField {
  type: string;
  value: string;
  primary?: boolean;
}

export interface ContactAddress {
  type: string;
  street?: string;
  city?: string;
  region?: string;
  postalCode?: string;
  country?: string;
}

/**
 * `photoUrl` is server-rendered and therefore not writable; a new photo goes up
 * as base64 instead (G16).
 */
export type ContactInput = Omit<
  Contact,
  'id' | 'updatedAt' | 'etag' | 'photoUrl' | 'isGroup' | 'members'
> & {
  photoBase64?: string;
  /** Delete the stored `PHOTO`. Omitting `photoBase64` alone leaves it as is. */
  photoRemove?: boolean;
};

/* --------------------------------------------------------------- calendar */

export interface Calendar {
  id: string;
  accountId: string;
  name: string;
  color: string;
  readOnly: boolean;
  href: string;
  visible: boolean;
  /**
   * This calendar has **at least one** share link.
   *
   * It is a boolean and not a link, because there is nothing to put here: a
   * link's URL is returned exactly once, at creation, and only `sha256(token)`
   * survives it. Emitting an address from a listing would hand the user
   * something that 404s the moment it is pasted into Google Calendar.
   *
   * It used to describe *the* feed, when a calendar had one token, one privacy
   * mode and one creation time. It now describes a **set**: privacy and
   * creation time are per link (`CalendarShareLink`), because "Alex may see my
   * busy times" and "the team may see full details" are two different grants
   * that must be revocable independently.
   */
  feedPublished?: boolean;
  /** How many share links exist, when the server counts them. */
  shareLinkCount?: number;
  /** The collection's CalDAV description, when the server stores one. */
  description?: string;
  /**
   * True for a calendar the server mirrors from an external `.ics` feed.
   *
   * A subscription is always `readOnly` — its events are the publisher's — but
   * the *collection* is ours: it can be renamed, recoloured and removed. The
   * three fields below only ever appear on one.
   */
  subscribed?: boolean;
  /** The external `.ics` URL this calendar mirrors. Never rendered as markup. */
  source?: string;
  /** When the feed was last fetched successfully. */
  lastSyncedAt?: ISODateTime;
  /** Non-empty when the last refresh failed; the server's own sentence. */
  syncError?: string;
}

/** `POST /calendars` — a calendar of our own, not a subscription. */
export interface CalendarInput {
  name: string;
  /** `#rrggbb`. Omitted lets the server pick from its palette. */
  color?: string;
  description?: string;
}

/**
 * `PATCH /calendars/{id}` — a *sparse* patch.
 *
 * An absent key leaves that property untouched; it is not the same as sending
 * an empty string, which clears it. Callers must therefore build this object
 * by omission rather than by spreading an existing `Calendar`.
 */
export interface CalendarPatch {
  name?: string;
  color?: string;
  description?: string;
}

/** `POST /calendar-subscriptions` — mirror an external `.ics` feed. */
export interface CalendarSubscriptionInput {
  name: string;
  /** http/https only; the server's SSRF guard has the final say. */
  url: string;
  color?: string;
}

/** `POST /address-books`. */
export interface AddressBookInput {
  name: string;
  description?: string;
}

/** `PATCH /address-books/{id}` — sparse, exactly like {@link CalendarPatch}. */
export interface AddressBookPatch {
  name?: string;
  description?: string;
}

/**
 * `full` publishes titles and details; `busy-only` publishes opaque blocks.
 *
 * The spelling is the backend's and doc 05 §105's, not a shorter one of our
 * own: `publishCalendarFeed` accepts exactly `full` and `busy-only` and 400s
 * on anything else, so the `busy` this used to say made the DEFAULT share --
 * busy-only -- the one that could never be published.
 */
export type CalendarFeedPrivacy = 'full' | 'busy-only';

/**
 * One named share link on a calendar — a bearer credential with a label.
 *
 * ## Why there are many, and why each has a name
 *
 * A single feed token per calendar makes every grant the same grant. Handing
 * the URL to a colleague and to a partner means one link in two places, and
 * withdrawing it from one withdraws it from both: the only lever, "regenerate",
 * breaks every subscriber at once. So people do not use it, and the calendar
 * stays shared with whoever has it.
 *
 * One link per recipient makes revocation *local*. `label` is therefore not
 * decoration — it is the only way the owner can tell which credential is which
 * six months later, because the URL itself is unreadable and unrecoverable.
 *
 * ## What `lastUsedAt` can and cannot say
 *
 * The server records when a token was last presented. It cannot record by
 * whom: the request carries no identity, and anyone the link was forwarded to
 * polls it exactly as its recipient does. So this answers "is this link still
 * live?" and never "who is reading my calendar?" — and the UI must not imply
 * otherwise. Absent means never presented.
 */
export interface CalendarShareLink {
  id: string;
  /** Who this link is for, in the owner's words. Max 80 chars server-side. */
  label: string;
  /** Per *link*, not per calendar: two people can be given two different views. */
  privacy: CalendarFeedPrivacy;
  createdAt: ISODateTime;
  /** Absent when the link has never been fetched. */
  lastUsedAt?: ISODateTime;
  /**
   * Absent for a link that lasts until revoked. Past it the link answers 404,
   * exactly as a revoked one does; it stays in this list until revoked.
   */
  expiresAt?: ISODateTime;
}

/** `GET /calendars/{id}/share-links` — oldest first, plus the per-calendar cap. */
export interface CalendarShareLinkList {
  links: CalendarShareLink[];
  /** The cap the server enforces; creating past it is a 409. */
  maxLinks: number;
}

/**
 * The answer to `POST /share-links`, and the **only** place `url` ever exists.
 *
 * The server keeps a hash, so this response cannot be reconstructed, re-read
 * or refetched. Anything holding it must hold it in memory and drop it: it
 * must not reach a persisted query cache, localStorage or a log.
 */
export interface MintedCalendarShareLink extends CalendarShareLink {
  url: string;
}

/** `POST /calendars/{id}/share-links`. */
export interface CalendarShareLinkInput {
  /** Required, 1–80 characters; the server 400s on empty or over-long. */
  label: string;
  privacy: CalendarFeedPrivacy;
  /**
   * Optional. Must be in the future and at most a year out (the server 400s
   * otherwise); omitted means the link lasts until revoked.
   */
  expiresAt?: ISODateTime;
}

export type EventStatus = 'confirmed' | 'tentative' | 'cancelled';
export type AttendeeStatus = 'accepted' | 'declined' | 'tentative' | 'needs-action';

export interface CalendarEvent {
  id: string;
  calendarId: string;
  uid: string;
  title: string;
  description?: string;
  location?: string;
  start: ISODateTime;
  end: ISODateTime;
  /** IANA zone the local time was authored in, e.g. "Europe/Stockholm". */
  startTz?: string;
  endTz?: string;
  allDay: boolean;
  status: EventStatus;
  /** RFC 5545 RRULE, verbatim. */
  recurrenceRule?: string;
  /** Present on an instance of a recurring series. */
  recurrenceId?: string;
  organizer?: Address;
  attendees: Attendee[];
  reminders: Reminder[];
  /**
   * The event's video call (RFC 7986 `CONFERENCE`), omitted when it has none.
   *
   * The server has already taken its own "Join video call" block out of
   * `description`, so the text the UI shows and edits never contains it, and
   * the UI never writes that block itself. On a write: absent keeps whatever
   * the event has, `''` removes the call, a URL sets or replaces it.
   */
  videoCallUrl?: string;
  etag?: string;
}

export interface Attendee extends Address {
  status: AttendeeStatus;
  optional?: boolean;
  /**
   * `RSVP=TRUE` — "the organizer is asking for an answer".
   *
   * Set on a guest the client has just added, so the server knows to solicit a
   * reply rather than merely list them. Absent on a guest the server has not
   * told us about either way; it is never inferred from `status`.
   */
  rsvp?: boolean;
}

export interface Reminder {
  /** Minutes before start. */
  minutesBefore: number;
  method: 'display' | 'email' | 'push';
}

/**
 * Which instances of a recurring series an edit or delete applies to (G5,
 * doc 05 §0.4). Sent on every write, including a non-recurring one, so the
 * backend never has to guess.
 */
export type EditScope = 'this' | 'this-and-future' | 'all';

export type CalendarEventInput = Omit<CalendarEvent, 'id' | 'uid' | 'etag'> & {
  editScope: EditScope;
};

export interface CalendarRangeQuery {
  accountId: string;
  calendarIds?: string[];
  start: ISODateTime;
  end: ISODateTime;
}

/**
 * iTIP invite embedded in a message (`text/calendar` part).
 *
 * Everything in `event` is **attacker-controlled text**: it came out of a MIME
 * part that anyone who can send mail can compose. It is rendered as text and
 * only as text — never as markup, and never injected into the message frame.
 *
 * `trusted` is the backend's verdict on whether the part really came from the
 * organizer it names (envelope/From alignment, DKIM). A `false` is not a
 * refusal to show the invitation; it is a fact about it that the reader is
 * told before they are offered a reply.
 */
export interface CalendarInvite {
  uid: string;
  /** `PUBLISH` is an announcement, not an invitation: it takes no reply. */
  method: 'REQUEST' | 'REPLY' | 'CANCEL' | 'PUBLISH';
  /**
   * Which MIME part carried the ICS. The reply endpoint re-reads that part
   * from the mailbox itself; the browser never sends ICS back up.
   */
  partId: string;
  event: CalendarEvent;
  /** The viewer's own `PARTSTAT`, when the ICS lists them at all. */
  myStatus?: AttendeeStatus;
  trusted: boolean;
  /** `SEQUENCE` of the ICS, when it carries one. */
  sequence?: number;
}

/**
 * `POST /accounts/{id}/calendars/{calendarId}/invite-reply`.
 *
 * Identifies the invitation by *where it is in the mailbox*, never by its
 * content: the server re-reads the ICS from `messageId`/`partId` and writes
 * the reply itself. A body carrying ICS from the browser would let the page
 * reply to something other than what the reader was shown.
 */
export interface InviteReplyInput {
  messageId: string;
  partId: string;
  status: Exclude<AttendeeStatus, 'needs-action'>;
  /**
   * The reader confirmed answering an invitation the server could not tie to
   * its organizer (`CalendarInvite.trusted === false`). Without it the server
   * refuses such a reply with 400 `field: confirm_unverified`; sent only when
   * true.
   */
  confirmUnverified?: boolean;
}

/* ------------------------------------------------------------------- push */

export interface PushPublicKey {
  /** VAPID application server key, base64url. */
  publicKey: string;
}

export interface PushSubscriptionInput {
  endpoint: string;
  keys: { p256dh: string; auth: string };
  /** Human label so the settings UI can list "Chrome on ThinkPad". */
  deviceLabel?: string;
}

export interface PushSubscriptionRecord {
  id: string;
  endpoint: string;
  deviceLabel?: string;
  createdAt: ISODateTime;
  lastUsedAt?: ISODateTime;
}

/**
 * Background notifications: the opt-in that stores the mail password on the
 * server, sealed under a server-held key, so it can check for new mail while
 * no tab is open. `needs_reconsent` means the server deleted the stored
 * password (a password change, or the mail server refusing it) and the
 * Notifications pane must say why.
 */
export type BackgroundPushState = 'off' | 'on' | 'needs_reconsent';
export type BackgroundPushReason = '' | 'password_changed' | 'auth_failed';

export interface BackgroundPushStatus {
  status: BackgroundPushState;
  reason: BackgroundPushReason;
  reasonAt?: ISODateTime | null;
  enabledAt?: ISODateTime | null;
  /** The consent wording version the server currently requires. */
  consentVersion: number;
  /**
   * True when the server also sends this owner's calendar reminders. The
   * current server says true whenever the enrolment is on, whatever consent
   * version it was made under (ADR 0064, owner decision 2026-09-22). Absent
   * from an older server that pushes new mail only, which reads as false.
   */
  calendarReminders?: boolean;
  /**
   * True when this server runs the task reminder scheduler. Independent of the
   * enrolment above: task reminders need no stored password and go to every
   * push subscription of the owner. Absent from an older server (false).
   */
  taskReminders?: boolean;
}

/** POST /push/background — exactly these keys (DisallowUnknownFields). */
export interface EnableBackgroundPushInput {
  password: string;
  /** The version of the explanation the person was shown and agreed to. */
  consentVersion: number;
}

/**
 * Background notifications for a connected (secondary) mailbox. Never the
 * primary, which is `BackgroundPushStatus` above.
 *
 * The server already keeps a connected mailbox's password, sealed under a key
 * from the owner's sign-in password; turning this on stores a second copy the
 * background watcher can open on its own. `needs_reconsent` means the server
 * deleted that copy, and `reason` says why.
 */
export type BackgroundAccountReason =
  | ''
  | 'auth_failed'
  | 'password_changed'
  | 'primary_password_changed'
  | 'host_changed';

/** One entry of GET /push/background/accounts, one per connected mailbox. */
export interface BackgroundAccountStatus {
  accountId: string;
  status: BackgroundPushState;
  reason: BackgroundAccountReason;
  reasonAt?: ISODateTime | null;
  enabledAt?: ISODateTime | null;
  /** The consent wording version the server currently requires. */
  consentVersion: number;
}

/**
 * POST /push/background/accounts/{id} — exactly these keys
 * (DisallowUnknownFields). `password` is the owner's SIGN-IN password, not the
 * connected mailbox's: it unlocks the copy of that mailbox's password the
 * server already keeps.
 */
export interface EnableBackgroundAccountInput {
  password: string;
  /** The version of the explanation the person was shown and agreed to. */
  consentVersion: number;
}

export interface NotificationPreferences {
  accountId: string;
  enabled: boolean;
  /** Folder ids that fire notifications; empty means Inbox only. */
  folderIds: string[];
  calendarReminders: boolean;
  /**
   * Task due-date reminders (ADR 0069 follow-up). Optional on PUT for the
   * server (absent keeps the stored value); always sent by this client.
   */
  taskReminders: boolean;
  /** Do-not-disturb window in local "HH:mm" form. */
  quietHours?: { start: string; end: string; tz?: string };
  snoozedUntil?: ISODateTime;
  /**
   * How much of the message a background (Web Push) notification carries.
   * `minimal` (the default) says only "New mail" (plus the mailbox's name for
   * a connected one) and leaves event and task titles out of reminders;
   * `full` adds the sender and subject, and those titles. The payload is
   * encrypted end to end (RFC 8291), so the push service cannot read either,
   * but a lock screen can.
   */
  pushPayloadDetail: 'minimal' | 'full';
  /**
   * Server-reported, never sent back meaningfully: whether this deployment
   * allows `full` at all (`notifications.allow_full_payload`). When false every
   * push is sent minimal whatever `pushPayloadDetail` says. Absent from an
   * older server, which means "unknown".
   */
  fullPayloadAllowed?: boolean;
  /**
   * Server-reported: the "All mailboxes" Do not disturb, when one is running
   * (#143). It silences this mailbox too, on top of `snoozedUntil`, and is
   * set only through `PUT /notifications/snooze`; the per-mailbox PUT ignores
   * it.
   */
  ownerSnoozedUntil?: ISODateTime;
}

/** `PUT /notifications/snooze` answer: the owner-wide snooze now in force. */
export interface OwnerSnooze {
  ownerSnoozedUntil?: ISODateTime;
}

/** `POST /push/test` answer (#197): how many of this sign-in's devices took it. */
export interface TestPushResult {
  sent: number;
  devices: number;
}

/* ------------------------------------------------------ user preferences */

/**
 * Settings → General, the half of it that follows the owner between devices
 * (`GET`/`PUT /preferences`).
 *
 * Only what describes the *person* is here. Theme, density and where the
 * reading pane sits describe the *screen*, so they stay in this browser's
 * storage and never reach the server — a dark phone beside a light desktop is
 * ordinary.
 */
/** The undo-send windows the server accepts (Gmail's four). */
export const UNDO_SEND_CHOICES = [5, 10, 20, 30] as const;
export type UndoSendSeconds = (typeof UNDO_SEND_CHOICES)[number];

export interface UserPreferences {
  /** BCP 47 tag for dates and numbers. `''` means this browser's own. */
  locale: string;
  /** IANA zone for displayed timestamps. `''` means this device's own. */
  timeZone: string;
  markReadEnabled: boolean;
  /** 0–60. Zero marks a message read the moment it opens. */
  markReadDelaySeconds: number;
  showSnippets: boolean;
  /**
   * How long a sent message is held so Undo can stop it: one of
   * {@link UNDO_SEND_CHOICES}. The server refuses anything else.
   */
  undoSendSeconds: UndoSendSeconds;
  /** Plain text, for the primary mailbox. */
  signature: string;
  signatureOnReply: boolean;
  /**
   * Conversation view (ADR 0077): group the list by conversation, Gmail-style.
   * Default on. Per person, so it follows them between devices.
   */
  conversationView: boolean;
  /**
   * The Jitsi server new video calls are created on, `https://host[:port][/path]`
   * with no trailing slash. `''` turns the "Add video call" button off.
   */
  videoCallUrl: string;
  /**
   * Put in front of every generated room name, `^[a-z0-9-]{0,24}$` and not
   * starting or ending with `-`. May be `''`.
   */
  videoCallRoomPrefix: string;
  /** Server-owned; absent until the first save. */
  updatedAt?: ISODateTime;
}

/* ------------------------------------------------ AI assistant (ADR 0065) */

/** One Claude model the settings page offers. The server owns the list. */
export interface AiModel {
  id: string;
  label: string;
  note: string;
}

/**
 * Settings → AI assistant. Per owner. The key itself is never here: the
 * server keeps it sealed and only ever says whether one is saved, its last
 * four characters and when it was saved.
 */
export interface AiSettings {
  /** True only while a key is saved AND the user turned the assistant on. */
  enabled: boolean;
  model: string;
  defaultModel: string;
  models: AiModel[];
  keySaved: boolean;
  /** The key's last four characters, for "sk-ant-…ab12". */
  keyHint?: string;
  keySavedAt?: ISODateTime;
  /** Why the SERVER erased the key, when it did. */
  keyRemovedReason?: 'password_changed' | 'signed_out_everywhere' | string;
  keyRemovedAt?: ISODateTime;
  lastUsedAt?: ISODateTime;
  /** The most text one request may carry, in characters. */
  maxInputChars: number;
}

export interface AiSettingsInput {
  enabled: boolean;
  model: string;
}

/** `POST /preferences/video-call/test`: the server tried the host itself. */
export interface VideoCallTestResult {
  /** False means the check ran and the host did not answer like a web server. */
  reachable: boolean;
  /** A sentence for a person, shown as it is. */
  message: string;
}

export interface AiKeyTestResult {
  ok: boolean;
  /** The model a request would use now: a `latest:*` choice arrives resolved. */
  model: string;
  /** Its display name ("Claude Sonnet 5.5"); absent from an older server. */
  modelName?: string;
}

/**
 * A family of Claude models. The server may name one this client has never
 * heard of, so the type stays open; `other` is the server's own bucket.
 */
export type AiModelFamily = 'opus' | 'sonnet' | 'haiku' | 'fable' | 'other' | (string & {});

/** One model the person's own key can use (`GET /ai/models`). The server owns every id. */
export interface AiListedModel {
  id: string;
  displayName: string;
  /** When Anthropic released it. Absent from the built-in fallback list. */
  createdAt?: ISODateTime;
  family: AiModelFamily;
  /** Not a plain family model (a preview, a special edition): "latest" never picks it. */
  variant?: boolean;
  maxInputTokens?: number;
  maxTokens?: number;
  /** `[]` when the model takes no effort parameter. */
  effortLevels?: string[];
  adaptiveThinking?: boolean;
}

/**
 * One "always use the latest" choice and what it resolves to now. `id` and
 * `displayName` are absent when the key has no model of that family.
 */
export interface AiLatestChoice {
  /** The value saved in `AiSettings.model`: `latest:sonnet` and so on. */
  choice: string;
  family: AiModelFamily;
  id?: string;
  displayName?: string;
}

/** The saved choice, resolved against the key's list. */
export interface AiSelectedModel {
  choice: string;
  /** What a request would use now; absent when nothing resolves. */
  id?: string;
  displayName?: string;
  /** The saved specific id is no longer in the key's list. */
  retired?: boolean;
  /** A sentence for the person, when `retired`. */
  notice?: string;
}

/**
 * `GET /ai/models`: the models the person's own key can use, newest first.
 * `source` says how fresh it is: `live` and `cache` are Anthropic's answer,
 * `stale` is the last answer kept while Anthropic cannot be reached, and
 * `fallback` is the server's built-in list.
 */
export interface AiModels {
  models: AiListedModel[];
  /** Order: sonnet, opus, haiku, then fable when the key has it. */
  latest: AiLatestChoice[];
  selected: AiSelectedModel;
  source: 'live' | 'cache' | 'stale' | 'fallback';
  /** When the list was fetched from Anthropic; absent for `fallback`. */
  fetchedAt?: ISODateTime;
  /** The ai error kind behind `stale` / `fallback` (e.g. `overloaded`). */
  upstreamError?: string;
}

/** What the compose window can ask for. */
export type AiAction = 'draft' | 'rewrite' | 'proofread' | 'reply' | 'review';

/** How a rewrite should change the text. */
export type AiStyle = 'clearer' | 'shorter' | 'formal' | 'friendlier';

/**
 * One assistant request. Only the fields the action uses may be set — the
 * server refuses the rest — and `thread` is only ever sent on a reply (and a
 * review of one).
 */
export interface AiAssistRequest {
  action: AiAction;
  style?: AiStyle;
  instruction?: string;
  text?: string;
  subject?: string;
  thread?: string;
  attachmentNames?: string[];
}

/**
 * What the reading pane can ask about a received message (ADR 0074). The
 * email itself is never in the request: the server reads it from the mailbox.
 */
export type AiReadAction = 'summarize' | 'explain' | 'ask';

export interface AiMessageAssistRequest {
  action: AiReadAction;
  /** Only with `ask`. */
  question?: string;
  /** Include the earlier messages this one quotes. */
  wholeThread?: boolean;
  /**
   * Read the whole conversation this message belongs to (ADR 0077): the
   * newest 10 messages, oldest first. Exclusive with `wholeThread`.
   */
  conversation?: boolean;
}

/** The first event of a message-assist stream: what was sent, and how. */
export interface AiMessageAssistMeta {
  /** The text was longer than the assistant takes; its end was cut. */
  truncated: boolean;
  /** The message quotes earlier messages ("Summarize the whole thread"). */
  hasQuoted: boolean;
  /** The quoted messages were included this time. */
  wholeThread: boolean;
  /**
   * The whole conversation was read (ADR 0077). Optional: a server that
   * predates conversations does not send it, which means it was not.
   */
  conversation?: boolean;
  inJunk: boolean;
  /** Web addresses that literally appear in the text sent: the only ones the panel links. */
  links: string[];
}

export interface AiAssistResult {
  /** Anthropic's stop_reason: `end_turn`, or `max_tokens` when cut short. */
  stopReason: string;
  /** The model that answered (a `latest:*` choice arrives resolved). */
  model?: string;
  /** Its display name, for the "via Claude Sonnet 5.5" line. */
  modelName?: string;
  /** Set when the saved model was retired and its family's latest answered instead. */
  modelNotice?: string;
}

/* --------------------------------------------------- remote content (0018) */

/**
 * Which of the two sanitizer modes produced the body that came back.
 *
 * Sent as `?remote=block|load` on `GET /messages/{id}` and echoed in
 * `remote_content.mode` as `blocked` / `loaded`. Two spellings for the same
 * axis is the backend's, not ours; both are modelled rather than smoothed
 * over, because guessing which one an endpoint wants is how a field drifts.
 */
export type RemoteMode = 'block' | 'load';

export type RemoteContentMode = 'blocked' | 'loaded';

/**
 * The persisted scopes of a remote-content grant (ADR 0018 §1).
 *
 * `svg_sender` and `svg_all` are a separate family (ADR 0018's SVG amendment,
 * backend `store/remote_content.go`): they never load anything by themselves,
 * they only let a load that is already allowed accept an SVG, which the server
 * sanitizes and the page rasterizes. `svg_all` is Settings → Privacy → "Allow
 * SVG images"; `svg_sender` is "Always allow SVG from …" on a message. There
 * is deliberately no SVG domain rule.
 */
export type RemoteContentRuleKind = 'sender' | 'domain' | 'all' | 'svg_sender' | 'svg_all';

/**
 * One persisted "always load" grant.
 *
 * `value` is a lower-cased address for `sender` and `svg_sender`, a domain for
 * `domain`, and the empty string for `all` and `svg_all` (the global toggles,
 * one row each per account).
 */
export interface RemoteContentRule {
  id: string;
  kind: RemoteContentRuleKind;
  value: string;
  createdAt: ISODateTime;
}

export interface RemoteContentRuleInput {
  kind: RemoteContentRuleKind;
  value: string;
}

/* --------------------------------------------------------- blocked senders */

/**
 * One blocked address. A block is an ordinary Sieve rule in the Filters list
 * (`blocked-…`, "Blocked sender: …", fileinto Junk + stop); `ruleId` is that
 * rule's id, so the two views can be matched up.
 */
export interface BlockedSender {
  address: string;
  ruleId: string;
}

export interface BlockedSenderList {
  senders: BlockedSender[];
}

/** `POST /accounts/{id}/blocked-senders` — keys match `blockSenderRequest`. */
export interface BlockSenderRequest {
  address: string;
  /** Also move mail already received from them to Junk. */
  moveExisting: boolean;
  /**
   * The folder being looked at, so existing mail from them there moves too.
   * The server always searches INBOX as well.
   */
  folderId?: string;
}

export interface BlockSenderResult {
  address: string;
  ruleId: string;
  /** What was moved to Junk; `{applied: [], failed: []}` without `moveExisting`. */
  moved: BulkResult;
}

/**
 * What the sanitizer did with this message's remote images.
 *
 * `trackersRemoved` is deliberately separate from `blocked`: a tracker is
 * removed outright and is **never** loadable, under any rule, so it can never
 * become a `loaded`. The bar's copy depends on telling the two apart.
 */
export interface RemoteContentInfo {
  mode: RemoteContentMode;
  /** Assets replaced by a sized placeholder. 0 in `loaded` mode. */
  blocked: number;
  /** Assets rewritten to a sealed proxy ref. 0 in `blocked` mode. */
  loaded: number;
  /** Scored trackers, removed entirely and never fetched. */
  trackersRemoved: number;
  /** Present when a persisted rule decided the mode. */
  appliedRule?: {
    kind: RemoteContentRuleKind;
    value: string;
    /**
     * Whether the receiving host's `Authentication-Results` backed the `From`.
     * A `sender`/`domain` rule is only *applied* when this is true; when it is
     * false the rule matched, the mode stayed blocked, and the bar says so.
     */
    verified: boolean;
  };
}

/* ----------------------------------------------- notification rules (0021) */

export type NotificationRuleKind = 'sender' | 'domain';
export type NotificationRuleAction = 'notify' | 'mute';

/**
 * One sender-layer notification rule.
 *
 * `value` is a lower-cased address (`kind: 'sender'`) or a domain
 * (`kind: 'domain'`); a domain value starting with `.` matches subdomains.
 * Validation of the value is the **server's** job — it owns normalisation and
 * the uniqueness constraint, and a second validator here would drift from it.
 */
export interface NotificationRule {
  id: string;
  accountId: string;
  kind: NotificationRuleKind;
  value: string;
  action: NotificationRuleAction;
  createdAt: ISODateTime;
}

export interface NotificationRuleInput {
  kind: NotificationRuleKind;
  value: string;
  action: NotificationRuleAction;
}

export type NotificationDecision = 'notify' | 'no';

/** One line of `GET …/notification-rules/preview`. */
export interface NotificationPreviewItem {
  messageId: string;
  folderId: string;
  from: Address;
  subject: string;
  date: ISODateTime;
  decision: NotificationDecision;
  /** The precedence line that decided, in words. Rendered verbatim. */
  reason: string;
  matchedRule?: NotificationRule;
}

export interface NotificationPreview {
  items: NotificationPreviewItem[];
  /** Mirrors the poll: a preview with notifications off explains every "no". */
  enabled: boolean;
  watchedFolders: string[];
}

/** One arrival the server decided the owner should be told about. */
export interface NewMailEvent {
  messageId: string;
  folderId: string;
  from: Address;
  subject: string;
  unseen: boolean;
  receivedAt: ISODateTime;
  /**
   * Arrived inside the owner's quiet hours: it still feeds the "N new
   * messages" pill, but no system or desktop notice may be raised for it.
   */
  quiet?: boolean;
}

/**
 * The interim poll's answer (ADR 0021 §"Interim polling").
 *
 * `watermark` is **opaque**: stored and handed back, never parsed. Its shape is
 * the server's business, and a client that read it would break the first time
 * the watched set changed.
 */
export interface NewMailPoll {
  watermark: string;
  events: NewMailEvent[];
  /** Arrivals the rules decided against. A count, never an id. */
  suppressed: number;
  /** False when notifications are off; the client stops polling on it. */
  enabled: boolean;
  watchedFolders: string[];
  /** Folders this poll could not read; their watermark is unchanged. */
  degradedFolders?: string[];
  /** The cadence the **server** asks for. Not a client constant. */
  pollAfterSeconds: number;
  /**
   * True while polling is the only delivery path. It goes false when IDLE +
   * SSE land, which is the settings page's signal to drop its "checked every
   * minute" line and the poller's signal to stand down.
   */
  interim: boolean;
  /**
   * True when the server pushes this mailbox's arrivals to every registered
   * browser (the background-notifications opt-in is on). A tab in a browser
   * holding a push subscription then leaves the system notice to the push.
   * Absent from an older server, which reads as false.
   */
  backgroundPush?: boolean;
}

/* ------------------------------------------------- live updates (ADR 0002) */

/**
 * Every event name the stream can carry.
 *
 * **Each one is a named SSE frame** (`event: new_mail`), so an `onmessage`
 * handler alone receives nothing at all: `EventSource` routes named frames to
 * `addEventListener(name, …)` and only unnamed ones to `onmessage`. Missing a
 * name here means silently missing that class of change forever, which is why
 * the list is exhaustive and typed rather than a loose string.
 */
export type StreamEventKind =
  | 'new_mail'
  | 'flags'
  | 'expunge'
  | 'folder_counts'
  | 'folder_list'
  | 'folder_invalidated'
  | 'account_status'
  | 'resync_required'
  /** Something in the account's task lists changed (ADR 0069); refetch them. */
  | 'tasks_changed'
  /** The account's labels changed (plan/17 §4); refetch the label list. */
  | 'labels_changed'
  | 'hello'
  | 'goodbye';

/** One message's new flag set, as raw IMAP flag atoms (`\Seen`). */
export interface StreamFlagChange {
  uid: number;
  flags: string[];
}

/**
 * One stream frame, camelCased from the wire like every other response.
 *
 * Identifiers only — no subject, sender or body. `messageIds` is minted by the
 * backend (which owns the encoding) and is positionally aligned with `uids`;
 * the client uses `folderId` and `messageIds` **as query keys** and never
 * decodes either, nor recombines a `uid` with `uidValidity` to build an id of
 * its own.
 */
export interface StreamEvent {
  id?: number;
  accountId?: string;
  folderId?: string;
  kind: StreamEventKind;
  messageIds?: string[];
  uids?: number[];
  flags?: StreamFlagChange[];
  /** Absolute folder counts after the change, not deltas. */
  unreadCount?: number;
  totalCount?: number;
  uidValidity?: number;
  /** `account_status` only: the account's new connection state. */
  status?: string;
  /** A short written phrase for `account_status`, `resync_required`, `goodbye`. */
  reason?: string;
  /**
   * The `Idempotency-Key` of the request that caused this change, so the tab
   * that made it can drop its own echo.
   */
  originMutationId?: string;
  at?: ISODateTime;
}

/* ------------------------------------------------------------------- tasks */

/**
 * The colours a task list can be (ADR 0069). A closed vocabulary, not a hex
 * value: the backend's CHECK constraint names exactly these, and each maps to
 * a `--color-tasklist-*` token so a theme can restate it. The contract test
 * (`tasks.contract.test.ts`) reads the migration to keep the two in step.
 */
export type TaskListColor = 'blue' | 'teal' | 'violet' | 'amber' | 'green' | 'red';

export const TASK_LIST_COLORS: readonly TaskListColor[] = [
  'blue',
  'teal',
  'violet',
  'amber',
  'green',
  'red',
];

/** One task list (`Work`, `Home`, `Shopping`). Per mailbox. */
export interface TaskList {
  id: string;
  name: string;
  color: TaskListColor;
  /** Optimistic concurrency: a PATCH must name the version it was based on. */
  version: number;
  openCount: number;
  completedCount: number;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
}

export interface TaskListsResponse {
  /** In the owner's order. */
  lists: TaskList[];
  maxLists: number;
  maxTasksPerList: number;
}

export interface TaskListInput {
  name: string;
  color?: TaskListColor;
}

export interface TaskListPatch {
  version: number;
  name?: string;
  color?: TaskListColor;
}

/** One task. */
export interface Task {
  id: string;
  listId: string;
  title: string;
  notes: string;
  /** `YYYY-MM-DD`, a wall-clock date with no zone (like Google Tasks). */
  dueDate: string | null;
  /** `HH:MM`, only ever alongside a date. */
  dueTime: string | null;
  starred: boolean;
  completedAt: ISODateTime | null;
  /**
   * The parent task's id (same list, always top-level), or null for a
   * top-level task. One level only, like Google Tasks.
   */
  parentId: string | null;
  /** The parent's title whenever `parentId` is set: smart views show it as context. */
  parentTitle: string | null;
  /** When to remind, relative to the due date (needs one), or null for none. */
  remind: TaskRemind | null;
  /** The instant `remind` names, computed by the server in the owner's zone. */
  remindAt: ISODateTime | null;
  /** The email this task was made from ("Add to Tasks"), or null. */
  email: TaskEmailLink | null;
  version: number;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
}

/**
 * Where a task's email is (ADR 0075). `messageId` and `folderId` are the ids
 * the mail view opens — where the email was when last seen; a UID changes
 * when mail moves, so "Open email" asks the server to resolve it
 * (`resolveEmail`), which re-finds a moved message by `rfcMessageId`.
 */
export interface TaskEmailLink {
  /** The task's own mailbox: a task goes to the mailbox its email is in. */
  accountId: string;
  folderId: string;
  messageId: string;
  /** The RFC 5322 Message-ID, without angle brackets; `''` when the email had none. */
  rfcMessageId: string;
}

/**
 * The email a new task is made from. `messageId` is the app's id of the row;
 * `rfcMessageId` is sent when the client knows it (an open message's
 * headers) and otherwise read by the server from the mailbox.
 */
export interface TaskEmailInput {
  messageId: string;
  rfcMessageId?: string;
}

/**
 * `GET …/tasks/items/{id}/email`: where the task's email is now.
 * - `found`: where it was; `moved`: re-found by Message-ID elsewhere (the
 *   link now points there); `gone`: in no folder (deleted);
 * - `unknown`: not found, but some folder did not answer, so it may exist.
 */
export interface TaskEmailResolution {
  status: 'found' | 'moved' | 'gone' | 'unknown';
  messageId: string | null;
  folderId: string | null;
  folderRole: string | null;
}

/**
 * A task's reminder, relative to its due date. The first four fit a task with
 * a time; the last two a date-only task (09:00 in the owner's zone). The
 * contract test holds this set to the migration's CHECK.
 */
export type TaskRemind =
  | 'at_time'
  | '10m_before'
  | '1h_before'
  | '1d_before'
  | 'day_of_9am'
  | 'day_before_9am';

export const TASK_REMIND_TIMED: readonly TaskRemind[] = ['at_time', '10m_before', '1h_before', '1d_before'];
export const TASK_REMIND_DATE_ONLY: readonly TaskRemind[] = ['day_of_9am', 'day_before_9am'];

export interface TaskInput {
  title: string;
  notes?: string;
  dueDate?: string | null;
  dueTime?: string | null;
  starred?: boolean;
  /** Makes it a subtask of this top-level task of the same list (added at the bottom). */
  parentId?: string;
  remind?: TaskRemind | null;
  /** The email this task is made from; the task must be in that email's mailbox. */
  email?: TaskEmailInput;
}

/**
 * A PATCH. An absent field is unchanged; `null` clears it (a cleared date
 * clears the time with it).
 */
export interface TaskPatch {
  version: number;
  title?: string;
  notes?: string;
  dueDate?: string | null;
  dueTime?: string | null;
  starred?: boolean;
  /**
   * Absent: unchanged, or mapped by the server when the due kind changes
   * (timed ↔ date-only). `null`: no reminder.
   */
  remind?: TaskRemind | null;
}

/**
 * Where a task goes: after `afterId` (`''` = first among its siblings) in
 * `listId` (default: its own). `parentId` absent keeps the parent within the
 * same list (and means top-level in another list); `null` outdents; an id
 * indents under that top-level task. `afterId` names a sibling in the target.
 */
export interface TaskMoveInput {
  listId?: string;
  afterId: string;
  parentId?: string | null;
}

/**
 * The cross-list views. `reminders` is not shown as a view: it is the in-app
 * fallback's read of reminders still worth sending, ordered by `remindAt`.
 */
export type TaskSmartView = 'starred' | 'due' | 'reminders';

/* --------------------------------------------- send later and snooze (ADR 0076) */

/**
 * Why a mailbox cannot schedule or snooze yet: the server needs the stored
 * background credential (ADR 0076 §1). `not_enrolled` — none; `needs_reconsent`
 * — it was withdrawn (password changed, host moved, auth failed);
 * `unavailable` — this server cannot store one at all. Any active enrolment
 * qualifies, whatever consent version it was made under (owner decision
 * 2026-09-23): nobody is asked for their password again.
 */
export type SchedulingReason = '' | 'not_enrolled' | 'needs_reconsent' | 'unavailable';

/** `GET /accounts/{id}/scheduling`. */
export interface SchedulingStatus {
  available: boolean;
  reason: SchedulingReason;
  /** Which enrolment it needs: the primary's (`/push/background`) or a connected one's. */
  kind: 'primary' | 'connected';
  /** The consent wording version a NEW enrolment is made under. */
  consentVersion: number;
}

/** `details` of `409 stored_credential_required` (schedule and snooze alike), camelCased. */
export interface StoredCredentialRequiredDetails {
  reason: Exclude<SchedulingReason, '' | 'unavailable'>;
  accountId: string;
  kind: 'primary' | 'connected';
  consentVersion: number;
}

export type ScheduledSendState = 'scheduled' | 'sending' | 'failed';

export type ScheduledSendErrorCode =
  | ''
  | 'credentials_missing'
  | 'credentials_rejected'
  | 'message_missing'
  | 'send_uncertain'
  | 'rejected'
  | 'retries_exhausted'
  | 'invalid_message'
  /** Waiting, not failed: the mailbox is over its hourly send rate (#83). */
  | 'rate_limited';

/** One open scheduled send (ADR 0076 §8). The message itself is the copy in `Scheduled`. */
export interface ScheduledSend {
  id: string;
  accountId: string;
  state: ScheduledSendState;
  sendAt: ISODateTime;
  attempts: number;
  nextAttemptAt: ISODateTime | null;
  errorCode: ScheduledSendErrorCode;
  error: string;
  /** The copy's app id in `Scheduled`; null when it could not be found. */
  messageId: string | null;
  rfcMessageId: string;
  subject: string;
  to: Address[];
  hasAttachments: boolean;
  createdAt: ISODateTime;
}

/** A message in `Scheduled` with no job (a crash between APPEND and INSERT, §7). */
export interface UnscheduledMessage {
  messageId: string;
  subject: string;
  to: Address[];
  date: ISODateTime;
}

/** `GET /accounts/{id}/scheduled`. */
export interface ScheduledSendList {
  items: ScheduledSend[];
  unscheduled: UnscheduledMessage[];
  /** The `Scheduled` folder's id; null until it was first created. */
  folderId: string | null;
}

/** `POST /accounts/{id}/scheduled`: exactly the Send body, plus when. */
export type ScheduleSendInput = SendInput & {
  /** RFC 3339, UTC: in the future, at most 366 days ahead. */
  sendAt: ISODateTime;
};

/** `POST …/scheduled/{id}/send-now`. */
export interface ScheduledSendNowResult {
  job: ScheduledSend;
  result: SendResult;
}

/** `POST …/scheduled/{id}/cancel`: the copy went to Drafts as `draftId`. */
export interface ScheduledCancelResult {
  job: ScheduledSend;
  draftId: string | null;
}

export type SnoozeState = 'snoozed' | 'waking' | 'failed';

/** One open snooze (ADR 0076 §12). */
export interface Snooze {
  id: string;
  accountId: string;
  state: SnoozeState;
  wakeAt: ISODateTime;
  /** Where it comes back to. */
  originalFolderId: string;
  /** Its app id in `Snoozed`; null when it could not be found. */
  messageId: string | null;
  rfcMessageId: string;
  subject: string;
  from: Address | null;
  date: ISODateTime | null;
  errorCode: string;
  error: string;
  createdAt: ISODateTime;
}

/** `POST /accounts/{id}/snoozes`. */
export interface SnoozeInput {
  /** 1..100 app message ids of this mailbox. */
  messageIds: string[];
  wakeAt: ISODateTime;
}

/** `POST /accounts/{id}/snoozes`: per message, snoozed or refused. */
export interface SnoozeResult {
  items: Snooze[];
  failed: Array<{ messageId: string; code: string; message: string }>;
}

/** `GET /accounts/{id}/snoozes`. */
export interface SnoozeList {
  items: Snooze[];
  folderId: string | null;
}

/** `POST …/snoozes/{id}/unsnooze`: where the message is now. */
export interface UnsnoozeResult {
  job: Snooze;
  messageId: string | null;
  folderId: string | null;
}

/* ------------------------------------------------------------------ labels */

/**
 * The colours a label can be (plan/17 §3). A closed vocabulary, as a task
 * list's is: the backend's CHECK constraint names exactly these, and each maps
 * to a `--color-label-*` token (tokens.css) so a theme can restate it.
 */
export type LabelColor =
  | 'gray'
  | 'blue'
  | 'teal'
  | 'green'
  | 'amber'
  | 'orange'
  | 'red'
  | 'violet'
  | 'pink';

export const LABEL_COLORS: readonly LabelColor[] = [
  'gray',
  'blue',
  'teal',
  'green',
  'amber',
  'orange',
  'red',
  'violet',
  'pink',
];

/** `renaming` / `deleting`: the keyword is still being rewritten on the server. */
export type LabelState = 'active' | 'renaming' | 'deleting';

/**
 * One label of ONE mailbox (plan/17 §1). On the mail server it is an IMAP
 * keyword on each labelled message; everything else lives in Postgres.
 */
export interface Label {
  id: string;
  name: string;
  keyword: string;
  /** Set while a rename is rewriting messages, or left as an alias (§4). */
  previousKeyword: string | null;
  color: LabelColor;
  showInSidebar: boolean;
  state: LabelState;
  /** Messages rewritten by the current rename/delete. */
  migratedCount: number;
  version: number;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
}

/** `POST /accounts/{id}/labels` */
export interface LabelInput {
  name: string;
  color?: LabelColor;
  showInSidebar?: boolean;
}

/** `PATCH /accounts/{id}/labels/{labelId}` — `version` is the one read. */
export interface LabelPatch {
  version: number;
  name?: string;
  color?: LabelColor;
  showInSidebar?: boolean;
}

/** `POST /accounts/{id}/labels/{labelId}/migrate`: one bounded step. */
export interface LabelMigrateResult {
  /** The row now; `null` once a delete has finished and the row is gone. */
  label: Label | null;
  done: boolean;
  rewritten: number;
  remainingInStep: number;
  foldersRefused: string[];
  filtersUpdated: number;
  filtersSkipped: boolean;
}

/** `POST /accounts/{id}/messages/labels` */
export interface LabelApplyInput {
  messageIds: string[];
  add: string[];
  remove: string[];
}

/* --------------------------------------------------------------- nextcloud */

/**
 * Nextcloud (plan 18, ADR 0084): large attachments as share links, and
 * "attach from Nextcloud". Per OWNER, at the root, like the AI assistant: one
 * linked Nextcloud account serves every mailbox's composer. Every type below
 * mirrors a struct in `backend/internal/httpapi/nextcloud_wire.go`, and
 * `nextcloud.contract.test.ts` holds the two to each other.
 */

/** Why a link is gone (`ncStatus.reason`). */
export type NextcloudLinkReason =
  | 'unlinked'
  | 'password_changed'
  | 'signed_out_everywhere'
  | 'rejected'
  | 'host_changed';

/** `ncQuota`: the linked account's storage, refreshed at most every 5 minutes. */
export interface NextcloudQuota {
  freeBytes: number;
  usedBytes: number;
  totalBytes: number;
  /** No quota on the Nextcloud side. */
  unlimited: boolean;
  /** When the figures were read. */
  at?: ISODateTime;
}

/** `ncCapabilities`: the part of `/cloud/capabilities` the feature obeys. */
export interface NextcloudCapabilities {
  /** Nextcloud's major version. */
  version: number;
  /** Public link shares are allowed at all (`files_sharing.public.enabled`). */
  publicLinks: boolean;
  /** Every link must carry a password: one is always generated. */
  passwordEnforced: boolean;
  passwordOptional: boolean;
  /** Every link must expire, within `expiryMaxDays`. */
  expiryEnforced: boolean;
  expiryMaxDays: number;
  chunkMaxBytes: number;
}

/** `ncSettings`: the person's choices, clamped to the capabilities. */
export interface NextcloudSettings {
  /** Files larger than this are offered as a link (1 MiB … 24 MiB, default 10 MiB). */
  linkThresholdBytes: number;
  /** 1 … 365, or 0 = never (when the instance allows it). */
  shareExpiryDays: number;
  /** Where uploads go, under the person's files; a `<yyyy-mm>` folder is added. */
  uploadFolder: string;
  askPassword: boolean;
  /**
   * Where *Save to Nextcloud* opens (ADR 0086): the folder the person last
   * chose, or the default when they last used the default (or never chose).
   * Response-only; remembered by the save itself, not by `PUT /settings`.
   */
  saveFolder: string;
  /** `<uploadFolder>/Received/<yyyy-mm>`, this month's default. */
  saveFolderDefault: string;
}

/** `ncLimits`: the deployment's ceilings. */
export interface NextcloudLimits {
  maxUploadBytes: number;
  /** The size of every chunk but the last, browser → us. */
  chunkBytes: number;
  minThresholdBytes: number;
  maxThresholdBytes: number;
}

/** `ncPending`: a Login Flow v2 still waiting for the person. */
export interface NextcloudPending {
  linkId: string;
  /** The instance's own login page, opened in a new window. */
  loginUrl: string;
  host: string;
  expiresAt: ISODateTime;
}

/**
 * `GET /nextcloud`, and the answer of every call that changes the link. It
 * never carries the app password, not even a hint of it.
 */
export interface NextcloudStatus {
  state: 'none' | 'active' | 'withdrawn';
  /** Why a link is gone. */
  reason?: NextcloudLinkReason;
  reasonAt?: ISODateTime;
  /** The app password was also deleted on the Nextcloud side. Absent: not attempted. */
  remoteRevoked?: boolean;
  baseUrl?: string;
  host?: string;
  loginName?: string;
  userId?: string;
  displayName?: string;
  linkedAt?: ISODateTime;
  lastUsedAt?: ISODateTime;
  lastVerifiedAt?: ISODateTime;
  quota?: NextcloudQuota;
  capabilities?: NextcloudCapabilities;
  settings: NextcloudSettings;
  limits: NextcloudLimits;
  /** A link flow started earlier, so a reload can resume the waiting state. */
  pending?: NextcloudPending;
  /** The deployment's placeholder for the URL field. */
  suggestedUrl?: string;
}

/** `POST /nextcloud/link` (201): open `loginUrl`, then poll `linkId`. */
export interface NextcloudLinkStarted {
  linkId: string;
  loginUrl: string;
  host: string;
  expiresAt: ISODateTime;
}

/** `POST /nextcloud/link/{id}/poll` answering 202: the person has not granted access yet. */
export interface NextcloudPollPending {
  state: 'pending';
  expiresAt: ISODateTime;
}

/** One poll: still waiting (202), or linked (200, with the new status). */
export type NextcloudPollResult = { pending: NextcloudPollPending } | { linked: NextcloudStatus };

/** `PUT /nextcloud/credential` — the "I already have an app password" path. Write-only. */
export interface NextcloudCredentialInput {
  baseUrl: string;
  loginName: string;
  appPassword: string;
}

/** `PUT /nextcloud/settings` — an omitted field keeps its value (as `/preferences`). */
export interface NextcloudSettingsInput {
  linkThresholdBytes?: number;
  shareExpiryDays?: number;
  uploadFolder?: string;
  askPassword?: boolean;
}

/** `DELETE /nextcloud`: the status, and a sentence about what was NOT touched. */
export interface NextcloudUnlinked {
  status: NextcloudStatus;
  message: string;
}

/** `ncEntry`: one row of a folder listing. Metadata only; no bytes reach the browser. */
export interface NextcloudEntry {
  name: string;
  /** Under the person's own files. */
  path: string;
  isDir: boolean;
  sizeBytes: number;
  contentType?: string;
  modifiedAt?: ISODateTime;
  fileId: number;
  /** Already shared by a link. */
  shared: boolean;
}

/** `GET /nextcloud/files?path=` */
export interface NextcloudListing {
  path: string;
  entries: NextcloudEntry[];
  quotaAvailableBytes?: number;
}

export type NextcloudUploadState =
  | 'uploading'
  | 'staged'
  | 'sent'
  | 'revoked'
  | 'expired'
  | 'deleting'
  | 'deleted'
  | 'failed';

/**
 * `ncUpload`: one row of "Files shared from mail" -- a file we uploaded
 * (`origin: 'uploaded'`), or an existing file we shared (`'existing'`, which
 * cleanup never deletes). Never carries the link's password.
 */
export interface NextcloudUpload {
  id: string;
  state: NextcloudUploadState;
  /**
   * `saved`: a received attachment saved to Nextcloud whose link the person
   * copied (ADR 0086) -- handed out directly, so it starts `sent`, and the
   * file is theirs: cleanup never deletes it.
   */
  origin: 'uploaded' | 'existing' | 'saved';
  filename: string;
  sizeBytes: number;
  contentType: string;
  path: string;
  fileId: number | null;
  shareId: string | null;
  shareUrl: string | null;
  expiresAt: ISODateTime | null;
  passwordSet: boolean;
  /** The mailbox it was sent from, once sent. */
  accountId: string | null;
  rfcMessageId: string | null;
  /** The subject it was sent with, written at send time. */
  subject: string | null;
  createdAt: ISODateTime;
  sentAt: ISODateTime | null;
  lastError: string | null;
  chunksDone: number;
  chunkCount: number;
}

/** `POST /nextcloud/uploads` */
export interface NextcloudUploadInput {
  filename: string;
  sizeBytes: number;
  contentType?: string;
}

/** `POST /nextcloud/uploads` (201): PUT chunks 1…`chunkCount` of `chunkBytes`, then finish. */
export interface NextcloudUploadReserved {
  upload: NextcloudUpload;
  chunkBytes: number;
  chunkCount: number;
  destinationPath: string;
}

/**
 * `POST /nextcloud/uploads/{id}/finish`. `expiryDays` overrides the setting
 * (0 = never, when allowed); `password: 'generate'` asks for a 14-character
 * password, answered once.
 */
export interface NextcloudFinishInput {
  expiryDays?: number;
  password?: 'generate';
}

/** `POST /nextcloud/files/share` — a link to a file already on Nextcloud. */
export interface NextcloudShareFileInput extends NextcloudFinishInput {
  path: string;
  /**
   * The link is for a file just saved from a received message (ADR 0086):
   * the row is `origin: 'saved'`, `state: 'sent'`, and names that message.
   */
  savedFrom?: NextcloudSavedFrom;
}

/** Which message a saved file came from, for "Files shared from mail". */
export interface NextcloudSavedFrom {
  accountId: string;
  subject: string;
  rfcMessageId: string;
}

/** `POST /nextcloud/folders` (201): *New folder* in the save picker. */
export interface NextcloudFolderCreated {
  path: string;
}

/**
 * `POST /accounts/{id}/messages/{id}/save-to-nextcloud` (ADR 0086): the
 * server reads each part from the mailbox and writes it to Nextcloud itself
 * -- the browser never carries the bytes.
 */
export interface NextcloudSaveInput {
  /** 1 … 50 part ids of this message. */
  partIds: string[];
  /** Omitted: `settings.saveFolder`. `''` is the Nextcloud root. */
  folder?: string;
  /** The Message-ID header, so a message moved meanwhile is found again (ADR 0075). */
  rfcMessageId?: string;
  /**
   * An email attached to the message (ADR 0092): `partIds` are then parts of
   * THAT email, and this is its path.
   */
  attachedPath?: string;
  /**
   * The client's own id for this save (1–64 of `A-Z a-z 0-9 _ -`), which
   * `cancelNextcloudSave` names: a proxy need not close the stream's
   * connection when the page stops reading it, so Cancel is said out loud.
   */
  saveId?: string;
}

/** One event of the save's `text/event-stream` answer. */
export type NextcloudSaveEvent =
  | { type: 'start'; folder: string; folderUrl: string }
  | {
      type: 'progress';
      partId: string;
      /** `reading`: from the mail server (no byte count); `uploading`: to Nextcloud. */
      phase: 'reading' | 'uploading';
      doneBytes: number;
      totalBytes: number;
    }
  /** The message had moved; it was found again by its Message-ID. */
  | { type: 'moved'; messageId: string; folderId: string }
  | {
      type: 'saved';
      partId: string;
      /** The name it has on Nextcloud: `report (2).pdf` when `report.pdf` was taken. */
      name: string;
      path: string;
      sizeBytes: number;
      fileId: number;
      renamed: boolean;
      /** The file in Nextcloud's own web UI. */
      openUrl: string;
    }
  /**
   * One part could not be saved: the status and error body the same refusal
   * would have had as a plain answer, read verbatim (snake_case details).
   */
  | { type: 'failed'; partId: string; status: number; error: ApiErrorBody }
  | { type: 'done'; saved: number; failed: number; folder: string; folderUrl: string };


/**
 * `ncShared`: the link, made. `password` is present exactly once -- in the
 * answer that generated it -- and is shown to the sender, never stored.
 */
export interface NextcloudShared {
  upload: NextcloudUpload;
  shareUrl: string;
  expiresAt: ISODateTime | null;
  password?: string;
}

/** `GET /nextcloud/uploads`: newest first, 50 a page. */
export interface NextcloudUploadPage {
  uploads: NextcloudUpload[];
  nextCursor?: string;
}

/** `GET /nextcloud/uploads` query. */
export interface NextcloudUploadQuery {
  state?: NextcloudUploadState;
  cursor?: string;
}

/** `PUT /nextcloud/uploads/{id}/chunks/{n}` options. */
export interface NextcloudChunkOptions {
  /** 0…1 of this chunk, as its bytes go out. */
  onProgress?: (fraction: number) => void;
  signal?: AbortSignal;
}

/**
 * One row of the per-protocol connection test (#521, ADR 0104):
 * `POST /accounts/probe` and `POST /accounts/{id}/probe`.
 *
 * `imap` is the login; `smtp` submission; `dav` contacts and calendar (one
 * base and one login for both); `filters` the mail host's admin path, which
 * never uses the person's password.
 */
export type ConnectionProbeProtocol = 'imap' | 'smtp' | 'dav' | 'filters';

/**
 * `skipped` = not tried because IMAP refused the password (one failed login
 * per test from the server's shared address, not three); `unavailable` = the
 * mail host does not offer it, or this server cannot test it.
 */
export type ConnectionProbeStatus = 'ok' | 'failed' | 'unavailable' | 'skipped';

export interface ConnectionProbeResult {
  protocol: ConnectionProbeProtocol;
  status: ConnectionProbeStatus;
  /** One sentence written by the server, shown verbatim. */
  detail: string;
}

export interface ConnectionProbe {
  /** Always four rows, in the order imap, smtp, dav, filters. */
  results: ConnectionProbeResult[];
}

/** `POST /accounts/probe`: a mailbox that is not connected yet. */
export interface ConnectionProbeRequest {
  email: string;
  password: string;
  /** As {@link CreateAccountRequest.provider}. */
  provider?: string;
}

/**
 * A group card (`KIND:group` / `X-ADDRESSBOOKSERVER-KIND:group`) as a filter
 * (doc 05 §2.4, #123). Read-only. Counts only: the server resolves the
 * `urn:uuid:` members against the book and never sends the references.
 */
export interface ContactGroup {
  /** The group card's own contact id; `contacts.list({ group })` filters by it. */
  id: string;
  name: string;
  /** Members the card names. */
  memberCount: number;
  /** Of those, how many are contacts in this book. */
  resolvedCount: number;
}

/**
 * One page of a book's contacts. Group cards are never rows; the first page
 * (no cursor) carries the book's groups. Absent from an older server.
 */
export interface ContactPage extends Paginated<Contact> {
  groups?: ContactGroup[];
}
